// ============================================
// DASHBOARD.JS — Panel principal del sistema
// Menú lateral con secciones y submenús, topbar,
// responsive: desktop, tablet, celular
// ============================================

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Home, Folder, FileText, Package, Briefcase, LayoutDashboard, LayoutGrid, Users, Megaphone,
  Sun, CalendarDays, ListChecks, CreditCard, Bot,
  ShoppingCart, FileEdit, BookOpen, ClipboardList, Layers, Link2, Plug,
  Wrench, Construction, Boxes, Camera,
  // FLUJOS-FIJOS: Workflow/UsersRound/Tags salieron con las pantallas dormidas.
  UserRound, Wallet, UserCog, Settings, FlaskConical,
  BarChart3, Activity, TrendingUp, Banknote,
  Tent, Tv, Building2, Contact,
  Target, Calendar, Library, FolderTree, KeyRound, LineChart, ArrowLeft,
  // SEGURIDAD-ENDPOINTS-ABIERTOS (2026-09-18): icono del Registro de accesos.
  ShieldAlert,
  Search,
  Eye,
} from 'lucide-react';
import { STORAGE_KEYS, storage, authHeaders } from './utils/storage';
import { tokens } from './styles/tokens';
import Clientes from './Clientes';
import ClientesNuevo from './ClientesNuevo';
import Directorio from './Directorio';
import Expedientes from './Expedientes';
import CalendarioCampo from './CalendarioCampo';
import ServiciosDelDia from './ServiciosDelDia';
import MantenimientosExp from './MantenimientosExp';
import {
  aplicarOrden, useSensoresDnD, ItemArrastrable, BarraPersonalizar,
  DndContext, closestCenter, SortableContext, arrayMove, verticalListSortingStrategy,
} from './components/PersonalizarMenu';
import Leads from './Leads';
import Proyectos from './Proyectos';
import Tareas from './Tareas';
import Gastos from './Gastos';
import Almacen from './Almacen';
import Compras from './Compras';
import TableroAuditor from './TableroAuditor';
import DashboardDirector from './DashboardDirector';
import CentroAgentes from './CentroAgentes';
import CanalAgentes from './components/CanalAgentes';
import Usuarios from './Usuarios';
import Inventario from './Inventario';
import Levantamiento from './Levantamiento';
import Calendario from './Calendario';
import CentroControl from './CentroControl';
import RecursosHumanos from './RecursosHumanos';
import Marketing from './Marketing';
import Configuracion from './Configuracion';
// FLUJOS-FIJOS: pantallas de workflows configurables dormidas (Entrega 1).
// import Workflows from './Workflows';
// import WorkflowsPersonas from './WorkflowsPersonas';
// import CatalogoEstados from './CatalogoEstados';
import CatalogoAdmin from './CatalogoAdmin';
import SyscomAdmin from './SyscomAdmin';
import EmpresaDatos from './EmpresaDatos';
import ListasPactadas from './ListasPactadas';
import Colecciones from './Colecciones';
import CotizadorConfig from './CotizadorConfig';
import TipoCambioConfig from './TipoCambioConfig';
import Previsualizador from './Previsualizador';
// SEGURIDAD-ENDPOINTS-ABIERTOS (2026-09-18): pantalla de consulta del registro de accesos.
import RegistroAccesos from './RegistroAccesos';
// BUSCADOR-PRO (2026-09-21): que busca el equipo en La Tienda y no encuentra.
import BusquedasSinResultado from './BusquedasSinResultado';
import AjustesVigilante from './AjustesVigilante';
// RECUPERAR-IMPORTES (2026-09-28): captura en tabla de los importes de facturas viejas.
import CapturaImportes from './CapturaImportes';
import CotizacionesLista from './components/CotizacionesLista';
import { API_BASE_CLI } from './components/helpersClientes';
import Tienda from './Tienda';
import MisLinks from './MisLinks';
import Cotizador from './Cotizador';
import Contratos from './Contratos';
import GruposProyectos from './GruposProyectos';
import MiDia from './MiDia';
import MiAgenda from './MiAgenda';
import Servicios from './Servicios';
import Requerimientos from './Requerimientos';
import BeepTV from './BeepTV';
// ExpoAlianza se renderiza via React Router en /expo

import AsistenteBeep, { espacioReservadoAsistente } from './components/AsistenteBeep';
import BarraInstalar from './components/BarraInstalar';
import { API_BASE } from './config/api';
// Alto de la barra de navegacion inferior de movil. Estaba repetido como '65px' en dos sitios;
// ahora es UNA constante porque el hueco reservado para el boton flotante depende de ella.
const ALTO_BARRA_INFERIOR = 65;
// TEMPORAL — quitar cuando SubirImagen este integrado en admin de productos (Commit B5)
import TestSubirImagen from './components/TestSubirImagen';
import PipelineCotizaciones from './components/PipelineCotizaciones';
import ConexionStatus from './components/ConexionStatus';

import MenuPerfil from './components/MenuPerfil';
import { ModalPerfil, ModalPassword } from './components/MiPerfil';
import { solicitarPermiso } from './utils/notificaciones';
import { puedeVerSeccion } from './utils/permisos';
// FIX-SESION-OBSERVABILIDAD: detectar 401 y redirigir al login con aviso.
import { fetchConSesion, manejarSesionExpirada } from './utils/sesion';
// PERM-3.A+B: gating unificado via context + componente protector
import { useUsuario } from './context/UsuarioContext';
import { RutaProtegida } from './components/RutaProtegida';
import BannerVerComo from './components/BannerVerComo';
// CALENDARIO-E1: ficha directa para el tecnico (abre SU expediente sin el modulo global).
import FichaExpediente from './components/expedientes/FichaExpediente';
import { OPC_DINERO } from './utils/dinero';

// PERM-3.A+B: aliases de rutas legacy → claves del catalogo MODULOS.
// Permite gatear las 30 rutas legacy sin cambiar la navegacion existente.
// Las rutas modernas (mitrabajo.*, operaciones.*, etc.) se gatean por identidad.
const ALIAS_PERMISO = {
  // CALENDARIO-E1: ruta virtual para abrir la ficha de UN expediente asignado
  // (tecnico restringido). Gatea por 'mitrabajo' (que el tecnico SI tiene), no
  // por 'operaciones.expedientes-todos'; el backend valida el acceso al
  // expediente puntual (requiereAccesoExpediente). Asi el tecnico entra a SU
  // proyecto sin ver la lista/kanban/actividad globales.
  'mitrabajo.mi-expediente':   'mitrabajo',
  // CALENDARIO-E2: la seccion propia 'calendario' gatea por el permiso del calendario.
  'calendario':                'operaciones.calendario',
  'compras':                   'almacen.compras',
  'almacen.compras':           'almacen.compras',
  'inicio.midia':              'mitrabajo.midia',
  'inicio.tareas':             'mitrabajo.tareas',
  'inicio.ejecutivo':          'dashboard.general',
  'inicio.vistaenvivo':        'dashboard.operaciones',
  'configuracion.usuarios':    'administracion.usuarios',
  'configuracion.catalogo':    'administracion.configuracion',
  'configuracion.empresa':     'administracion.configuracion',
  'requerimientos.agenda':     'mitrabajo.agenda',
  'requerimientos.nuevo':      'operaciones.expedientes-todos',
  'requerimientos.enproceso':  'operaciones.expedientes-todos',
  'requerimientos.historial':  'operaciones.expedientes-todos',
  'proyectos.activos':         'operaciones.expedientes-todos',
  'proyectos.calendario':      'operaciones.calendario',
  'proyectos.grupos':          'operaciones.expedientes-todos',
  'inventario.almacen':        'almacen.inventario',
  'rrhh.asistencias':          'administracion.rrhh',
  'clientes.empresas':         'comercial.clientes',
  'clientes.contactos':        'comercial.directorio',
  'clientes.contratos':        'comercial.clientes',
  'servicios.correctivos':     'operaciones.servicios',
  'servicios.mantenimientos':  'operaciones.mantenimientos',
  'servicios.todos':           'operaciones.servicios',
  'servicios.monitoreo':       'dashboard.operaciones',
  'comercial.directorio':      'comercial.clientes',
  'comercial.cotizaciones':    'cotizaciones.tienda',
  'comercial.levantamientos':  'operaciones.levantamientos',
  'mitrabajo.agentes-chat':    'mitrabajo.agentes',
  // CO-2: el previsualizador se llega desde La Tienda; se gatea con su permiso.
  'cotizaciones.previsualizador': 'cotizaciones.tienda',
  'otros.leads':               'comercial.prospectos',
  'otros.gruposproyectos':     'operaciones.expedientes-todos',
  'otros.configuracion':       'administracion.configuracion',
};
import {
  ComposedChart, Bar, Line, XAxis, YAxis, Tooltip,
  CartesianGrid, ResponsiveContainer,
} from 'recharts';

// ============================================
// COLORES DEL SISTEMA
// ============================================
const colores = {
  principal: '#5c9adb',
  oscuro: '#2f5f9e',
  claro: '#8fb8e8',
  gris: '#4e5357',
  grisClaro: '#5e666e',
  amarillo: '#dcde74',
  fondo: '#f3f4f4',
  rojo: '#e05c5c',
};

// ============================================
// MENÚ CON SECCIONES Y SUBMENÚS
// ============================================
const secciones = [
  {
    id: 'mitrabajo', label: 'Mi Trabajo', icono: '📓', Icono: Home, color: '#5c9adb',
    subs: [
      { id: 'midia', label: 'Mi día', icono: '☀️', Icono: Sun },
      { id: 'agenda', label: 'Mi agenda', icono: '📒', Icono: CalendarDays },
      { id: 'tareas', label: 'Mis tareas', icono: '✅', Icono: ListChecks },
      { id: 'gastos', label: 'Mis gastos', icono: '💳', Icono: CreditCard },
      { id: 'agentes', label: 'Mis agentes', icono: '🤖', Icono: Bot },
    ],
  },
  {
    // id 'operaciones' se conserva por compatibilidad con el orden personal de usuarios
    // FASE 2A: sin submenús — click directo navega al módulo nuevo de Expedientes (CRM).
    // Los submenús viejos (calendario/servicios/mantenimientos/etc.) se moverán a "Servicios" en una fase posterior.
    // esFlat: true permite que el filtro de seccionesVisibles no la oculte por tener subs vacío.
    id: 'operaciones', label: 'Expedientes', icono: '📁', Icono: Folder, color: '#f4a261',
    subs: [], esFlat: true,
  },
  {
    // CALENDARIO-E2: seccion propia del calendario de campo (agenda por tecnico).
    // Flat (click directo). Gateada por el permiso 'operaciones.calendario' (ver
    // el filtro de seccionesVisibles): direccion/gerencia/operaciones lo tienen,
    // el tecnico NO. Su ruta se resuelve en renderContenido (clave === 'calendario').
    id: 'calendario', label: 'Calendario', icono: '📅', Icono: CalendarDays, color: '#5c9adb',
    subs: [], esFlat: true, permiso: 'operaciones.calendario',
  },
  {
    id: 'cotizaciones', label: 'Cotizaciones', icono: '📝', Icono: FileText, color: '#7b8eb3',
    subs: [
      { id: 'tienda', label: 'La Tienda', icono: '🛒', Icono: ShoppingCart },
      { id: 'en-cotizacion', label: 'Cotizaciones', icono: '📄', Icono: FileEdit },
      { id: 'catalogo', label: 'Mis Productos', icono: '📦', Icono: BookOpen },
      { id: 'listas', label: 'Listas pactadas', icono: '📋', Icono: ClipboardList },
      { id: 'colecciones', label: 'Colecciones', icono: '🗂️', Icono: Layers },
      { id: 'mis-links', label: 'Mis links', icono: '🔗', Icono: Link2 },
      { id: 'tipo-cambio', label: 'Tipo de cambio', icono: '💱', Icono: Banknote },
      // CO-1: oculto del menu por ser mock no conectado a API real.
      //       Los archivos siguen en disco para futura migracion.
      // { id: 'configuracion', label: 'Configuración', icono: '⚙️' },
      { id: 'syscom', label: 'Conexión Syscom', icono: '🔌', Icono: Plug },
    ],
  },
  {
    id: 'almacen', label: 'Almacén', icono: '📦', Icono: Package, color: '#9b59b6',
    // SUB-ALMACENES (2026-10-19): Central · Oficina · Tecnicos · Instalados (antes 5 items que abrian lo mismo).
    subs: [
      { id: 'inventario', label: 'Central', icono: '📋', Icono: Boxes },
      { id: 'oficina', label: 'Oficina', icono: '🏢', Icono: Construction },
      { id: 'tecnicos', label: 'Resguardos', icono: '👤', Icono: Wrench },
      { id: 'equiposinstalados', label: 'Instalados', icono: '📷', Icono: Camera },
    ],
  },
  {
    // ALMACEN v3 (2026-10-21, A7): COMPRAS como seccion propia (antes era una pestana del Almacen). La gatea el permiso
    // almacen.compras de la matriz (direccion y compras), igual que Calendario gatea por el suyo.
    id: 'compras', label: 'Compras', icono: '🛒', Icono: ShoppingCart, color: '#b45309',
    subs: [], esFlat: true, permiso: 'almacen.compras',
  },
  {
    id: 'administracion', label: 'Administración', icono: '💼', Icono: Briefcase, color: '#e91e8c',
    subs: [
      { id: 'rrhh', label: 'Recursos Humanos', icono: '👤', Icono: UserRound },
      { id: 'finanzas', label: 'Finanzas', icono: '💰', Icono: Wallet },
      { id: 'usuarios', label: 'Usuarios', icono: '👥', Icono: UserCog },
      { id: 'configuracion', label: 'Configuración', icono: '⚙️', Icono: Settings },
      // AUDITOR DEL ALMACEN (2026-10-21): series sin destino, faltantes por persona, devoluciones vencidas, consumos atipicos,
      // series fuera de ubicacion y alertas de cobro. Solo direccion (la matriz: administracion.auditor; el backend soloDireccion).
      // El backend contesta 403 a quien no es direccion: dejarPasarSub solo lo deja ver con acceso total (candado de negocio extra, no un gate por rol).
      { id: 'auditor', label: 'Auditor del almacén', icono: '🕵️', Icono: ClipboardList },
      // DATOS DE LA EMPRESA (2026-09-07): emisor, cuentas bancarias y textos de garantia.
      { id: 'empresa', label: 'Datos de la empresa', icono: '🏢', Icono: Building2 },
      // SEGURIDAD-ENDPOINTS-ABIERTOS (2026-09-18): quien ha entrado a las direcciones sensibles.
      // Solo direccion (y el servidor responde 403 por su cuenta a cualquier otro).
      { id: 'registro-accesos', label: 'Registro de accesos', icono: '🛡️', Icono: ShieldAlert },
      { id: 'busquedas-sin-resultado', label: 'Búsquedas sin resultado', icono: '🔍', Icono: Search },
      { id: 'ajustes-vigilante', label: 'Ajustes del vigilante', icono: '👁️', Icono: Eye },
      // RECUPERAR-IMPORTES (2026-09-28): los 149 expedientes facturados sin saber cuanto
      // valieron. Se vacia sola conforme se capturan.
      { id: 'capturar-importes', label: 'Capturar importes', icono: '💰', Icono: Wallet },
      // FLUJOS-FIJOS (Entrega 1, 2026-07-07): las pantallas del sistema de
      // workflows configurables quedan DORMIDAS (codigo y datos intactos, solo
      // se quitan del menu). Los 3 flujos fijos las reemplazan. Para revivirlas
      // basta descomentar estas 3 lineas (y sus ramas en rutasConModulo +
      // renderContenido + permisos.js + Usuarios.js).
      // { id: 'workflows', label: 'Workflows', icono: '🔀', Icono: Workflow },
      // { id: 'workflows-personas', label: 'Personas por workflow', icono: '🧩', Icono: UsersRound },
      // { id: 'estados', label: 'Estados', icono: '🏷️', Icono: Tags },
      // TEMPORAL — quitar cuando SubirImagen este integrado en admin de productos (Commit B5)
      { id: 'test-subir-imagen', label: 'Test SubirImagen', icono: '🧪', Icono: FlaskConical },
    ],
  },
  {
    id: 'dashboard', label: 'Dashboard', icono: '📊', Icono: LayoutDashboard, color: '#2f5f9e',
    subs: [
      { id: 'general', label: 'Vista general', icono: '📊', Icono: BarChart3 },
      { id: 'operaciones', label: 'Operaciones', icono: '⚙️', Icono: Activity },
      { id: 'comercial', label: 'Comercial', icono: '💼', Icono: TrendingUp },
      { id: 'finanzas', label: 'Finanzas', icono: '💰', Icono: Banknote },
    ],
  },
  {
    id: 'aplicativos', label: 'Aplicativos', icono: '📱', Icono: LayoutGrid, color: '#10b981',
    subs: [
      { id: 'expo', label: 'Expo Alianza', icono: '🎪', Icono: Tent },
      { id: 'beeptv', label: 'BeepTV', icono: '📺', Icono: Tv },
    ],
  },
  {
    id: 'comercial', label: 'Clientes', icono: '👤', Icono: Users, color: '#dcde74',
    subs: [
      { id: 'clientes', label: 'Clientes', icono: '🏢', Icono: Building2 },
      { id: 'directorio', label: 'Directorio', icono: '📇', Icono: Contact },
    ],
  },
  {
    id: 'marketing', label: 'Marketing', icono: '📣', Icono: Megaphone, color: '#a78bfa',
    subs: [
      { id: 'campanas', label: 'Campañas', icono: '🎯', Icono: Target },
      { id: 'calendario', label: 'Calendario', icono: '📅', Icono: Calendar },
      { id: 'biblioteca', label: 'Biblioteca', icono: '📚', Icono: Library },
      { id: 'recursos', label: 'Recursos', icono: '🗂️', Icono: FolderTree },
      { id: 'credenciales', label: 'Credenciales', icono: '🔑', Icono: KeyRound },
      { id: 'metricas', label: 'Métricas', icono: '📊', Icono: LineChart },
      { id: 'agentes', label: 'Agentes IA', icono: '🤖', Icono: Bot },
    ],
  },
];

// UI-3: render unificado del icono de una seccion/sub. Usa el componente Lucide
// del campo `Icono`; si no existe, cae al emoji `icono` como respaldo.
function IconoSeccion({ item, size = 20, color }) {
  const Ico = item?.Icono;
  if (Ico) return <Ico size={size} color={color} strokeWidth={2} style={{ verticalAlign: 'middle', flexShrink: 0 }} />;
  return <span style={{ fontSize: size, color }}>{item?.icono}</span>; // respaldo emoji
}

// Mapa de rutas activas a componentes
const rutasConModulo = {
  // MI TRABAJO
  'mitrabajo.midia': true,
  'mitrabajo.agenda': true,
  'mitrabajo.tareas': true,
  'mitrabajo.gastos': true,
  'mitrabajo.agentes': true,
  // EXPEDIENTES (antes OPERACIONES + sub-items migrados desde PROYECTOS)
  'operaciones.expedientes-todos': true,
  'operaciones.calendario': true,
  'operaciones.servicios': true,
  'operaciones.mantenimientos': true,
  'operaciones.programar': true,
  'operaciones.nueva-instalacion': true,
  'operaciones.levantamientos': true,
  // COTIZACIONES
  'cotizaciones.tienda': true,
  'cotizaciones.en-cotizacion': true,
  'cotizaciones.catalogo': true,
  'cotizaciones.listas': true,
  'cotizaciones.colecciones': true,
  'cotizaciones.mis-links': true,
  // CO-1: oculto del menu por ser mock no conectado a API real
  // 'cotizaciones.configuracion': true,
  'cotizaciones.syscom': true,
  // ALMACEN
  'almacen.inventario': true,
  'almacen.oficina': true,
  'almacen.tecnicos': true,
  'almacen.equiposinstalados': true,
  'compras': true,
  'almacen.compras': true,
  // COMERCIAL
  'comercial.clientes': true,
  'comercial.directorio': true,
  // CO-1: oculto del menu por ser mock no conectado a API real (PipelineCotizaciones)
  // 'comercial.cotizaciones': true,
  'comercial.prospectos': true,
  // ADMINISTRACION
  'administracion.rrhh': true,
  'administracion.finanzas': true,
  'administracion.usuarios': true,
  'administracion.auditor': true,
  'administracion.configuracion': true,
  'administracion.empresa': true,
  // FLUJOS-FIJOS: workflows/estados dormidos (ver nota en subs de administracion)
  // 'administracion.workflows': true,
  // 'administracion.workflows-personas': true,
  // 'administracion.estados': true,
  'administracion.test-subir-imagen': true,
  // MARKETING
  'marketing.campanas': true,
  'marketing.calendario': true,
  'marketing.biblioteca': true,
  'marketing.recursos': true,
  'marketing.credenciales': true,
  'marketing.metricas': true,
  'marketing.agentes': true,
  // DASHBOARD
  'dashboard.general': true,
  'dashboard.operaciones': true,
  'dashboard.comercial': true,
  'dashboard.finanzas': true,
  // APLICATIVOS
  'aplicativos.expo': true,
  'aplicativos.beeptv': true,
  // Compatibilidad legacy (mantener para no romper navegación existente)
  'inicio.midia': true,
  'inicio.tareas': true,
  'configuracion.usuarios': true,
  'configuracion.catalogo': true,
  'configuracion.empresa': true,
  'requerimientos.agenda': true,
  'requerimientos.nuevo': true,
};

// ============================================
// SWIPE / TABS DE SUBS
// ============================================
function SwipeSubs({ subs, subActivo, onCambiarSub, children }) {
  const [touchStart, setTouchStart] = React.useState(null);
  const [touchStartY, setTouchStartY] = React.useState(null);
  const [touchDelta, setTouchDelta] = React.useState(0);
  const [animando, setAnimando] = React.useState(false);
  const [scrolling, setScrolling] = React.useState(false);

  const indiceActual = subs.findIndex(s => s.id === subActivo);

  const handleTouchStart = (e) => {
    setTouchStart(e.touches[0].clientX);
    setTouchStartY(e.touches[0].clientY);
    setTouchDelta(0);
    setScrolling(false);
  };
  const handleTouchMove = (e) => {
    if (touchStart === null) return;
    const dx = e.touches[0].clientX - touchStart;
    const dy = e.touches[0].clientY - touchStartY;
    if (!scrolling && Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 10) {
      setScrolling(true);
      return;
    }
    if (scrolling) return;
    setTouchDelta(dx);
  };
  const handleTouchEnd = () => {
    if (!scrolling && Math.abs(touchDelta) > 60) {
      const siguiente = touchDelta < 0 ? subs[indiceActual + 1] : subs[indiceActual - 1];
      if (siguiente) {
        setAnimando(true);
        onCambiarSub(siguiente.id);
        setTimeout(() => setAnimando(false), 300);
      }
    }
    setTouchStart(null); setTouchStartY(null); setTouchDelta(0); setScrolling(false);
  };

  return (
    <div
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{
        // C2-B: solo aplicar transform al deslizar. translateX(0px) permanente
        // crea contenedor de bloque que ROMPE position:sticky de los hijos
        // (mismo bug que scale(1) en .pagina-entrada). En reposo -> none.
        transform: touchDelta !== 0
          ? `translateX(${Math.max(-30, Math.min(30, touchDelta * 0.1))}px)`
          : 'none',
        transition: animando ? 'transform 0.3s ease' : 'none',
        width: '100%',
        minHeight: '100%',
      }}
    >
      {children}
    </div>
  );
}

function IndicesSubs({ subs, subActivo }) {
  if (!subs || subs.length <= 1) return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'center', gap: 6, padding: '8px 0' }}>
      {subs.map(s => (
        <div key={s.id} style={{
          width: s.id === subActivo ? 18 : 6, height: 6, borderRadius: 99,
          background: s.id === subActivo ? tokens.primary : tokens.borderStrong,
          transition: 'all 0.3s ease',
        }} />
      ))}
    </div>
  );
}

function TabsSubs({ subs, subActivo, onCambiarSub }) {
  if (!subs || subs.length <= 1) return null;
  return (
    <div style={{
      display: 'flex', gap: 4, padding: '8px 16px',
      background: tokens.surface, borderBottom: `1px solid ${tokens.borderSubtle}`,
      overflowX: 'auto',
    }}>
      {subs.map(s => {
        const activa = s.id === subActivo;
        return (
          <button
            key={s.id}
            type="button"
            onClick={() => onCambiarSub(s.id)}
            onMouseEnter={(e) => { if (!activa) e.currentTarget.style.background = tokens.primary100; }}
            onMouseLeave={(e) => { if (!activa) e.currentTarget.style.background = tokens.surfaceSubtle; }}
            style={{
              padding: '6px 14px', borderRadius: 99, border: 'none',
              background: activa ? tokens.primary : tokens.surfaceSubtle,
              color: activa ? tokens.textOnPrimary : tokens.textSecondary,
              fontSize: 13, fontWeight: activa ? 600 : 400,
              cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.2s ease',
              fontFamily: "'Poppins', sans-serif",
            }}
          >
            <IconoSeccion item={s} size={16} /> {s.label}
          </button>
        );
      })}
    </div>
  );
}

// Secciones mostradas en barra inferior celular
const seccionesCelular = ['mitrabajo', 'operaciones', 'cotizaciones', 'comercial'];

// ============================================
// EQUIPO
// ============================================
const equipo = [
  { nombre: 'Miguel G.', rol: 'Tecnico',     activo: true  },
  { nombre: 'Pedro R.',  rol: 'Tecnico',     activo: true  },
  { nombre: 'Laura S.',  rol: 'Supervisora', activo: true  },
  { nombre: 'Carlos V.', rol: 'Comercial',   activo: false },
  { nombre: 'Omar L.',   rol: 'Tecnico',     activo: false },
];

// ============================================
// DATOS DE LA GRÁFICA SEMANAL
// ============================================
const graficaDatos = [
  { dia: 'Lun', proyectos: 4, leads: 2 },
  { dia: 'Mar', proyectos: 6, leads: 4 },
  { dia: 'Mie', proyectos: 3, leads: 1 },
  { dia: 'Jue', proyectos: 8, leads: 5 },
  { dia: 'Vie', proyectos: 5, leads: 3 },
  { dia: 'Sab', proyectos: 2, leads: 1 },
  { dia: 'Hoy', proyectos: 7, leads: 2 },
];

// ============================================
// DATOS DEL DASHBOARD EJECUTIVO — 4 períodos × 4 tabs
// ============================================
const datosPorPeriodo = {
  hoy: {
    finanzas: {
      facturado: 28500, cobrado: 18500, porCobrar: 7000, vencido: 3000,
      grafica: [
        { sem: 'S1', ingresos: 18000, meta: 45000 },
        { sem: 'S2', ingresos: 32000, meta: 90000 },
        { sem: 'S3', ingresos: 41000, meta: 135000 },
        { sem: 'S4', ingresos: 28500, meta: 180000 },
      ],
      anticiposCobrados: { count: 3, monto: 68000 },
      finiquitosPendientes: { count: 5, monto: 143000 },
      proyectosFiniquito: [
        { nombre: 'Farma Plus – HVAC',  anticipo: 48000, finiquito: 52000, total: 100000 },
        { nombre: 'C. Com. Nexus',      anticipo: 32000, finiquito: 38000, total:  70000 },
        { nombre: 'Of. Grupo Regio',    anticipo: 20000, finiquito: 25000, total:  45000 },
      ],
      metaActual: 28500, metaMensual: 180000,
      vendedores: [
        { nombre: 'Carlos V.', monto: 18500 },
        { nombre: 'Laura S.',  monto:  7000 },
        { nombre: 'Pedro R.',  monto:  3000 },
      ],
    },
    comercial: {
      pipelineValor: 185000,
      grafica: [
        { sem: 'S1', leads: 3, conversion: 33 },
        { sem: 'S2', leads: 5, conversion: 40 },
        { sem: 'S3', leads: 4, conversion: 50 },
        { sem: 'S4', leads: 2, conversion: 42 },
      ],
      leadsNuevos: 2, leadsCalificados: 1, proyectosGenerados: 1, tasaConversion: 42,
      funnel: [
        { etapa: 'Lead', count: 6 }, { etapa: 'Seguimiento', count: 4 },
        { etapa: 'Calificado', count: 3 }, { etapa: 'Proyecto', count: 2 },
      ],
    },
    operacion: {
      proyectosActivos: 14,
      grafica: [
        { sem: 'S1', completados: 2, eficiencia: 75 }, { sem: 'S2', completados: 3, eficiencia: 78 },
        { sem: 'S3', completados: 4, eficiencia: 80 }, { sem: 'S4', completados: 1, eficiencia: 78 },
      ],
      flujo: { lead: 6, seguimiento: 4, proyecto: 14, ejecucion: 7, cierre: 2 },
      retrasados: 3, enTiempo: 11, eficiencia: 78,
    },
    inventario: {
      totalEquipos: 48,
      grafica: [
        { sem: 'S1', movimientos: 4, temporales: 8 }, { sem: 'S2', movimientos: 6, temporales: 10 },
        { sem: 'S3', movimientos: 5, temporales: 9 },  { sem: 'S4', movimientos: 3, temporales: 11 },
      ],
      enAlmacen: 22, asignados: 15, temporalesActivos: 8, sinRecuperar: 3,
      equiposCriticos: [
        { nombre: 'Multímetro Fluke 117', responsable: 'Miguel G.', dias: 12 },
        { nombre: 'Taladro Dewalt 20V',   responsable: 'Omar L.',   dias: 8  },
        { nombre: 'Nivel láser Bosch',    responsable: 'Pedro R.',  dias: 5  },
      ],
    },
  },
  semana: {
    finanzas: {
      facturado: 142000, cobrado: 98000, porCobrar: 32000, vencido: 12000,
      grafica: [
        { sem: 'L', ingresos: 18000, meta: 25714 }, { sem: 'M', ingresos: 22000, meta: 51428 },
        { sem: 'X', ingresos: 15000, meta: 77142 }, { sem: 'J', ingresos: 28000, meta: 102857 },
        { sem: 'V', ingresos: 19000, meta: 128571 },{ sem: 'S', ingresos: 8000,  meta: 154285 },
        { sem: 'H', ingresos: 32000, meta: 180000 },
      ],
      anticiposCobrados: { count: 4, monto: 92000 },
      finiquitosPendientes: { count: 5, monto: 143000 },
      proyectosFiniquito: [
        { nombre: 'Farma Plus – HVAC',  anticipo: 48000, finiquito: 52000, total: 100000 },
        { nombre: 'C. Com. Nexus',      anticipo: 32000, finiquito: 38000, total:  70000 },
        { nombre: 'Of. Grupo Regio',    anticipo: 20000, finiquito: 25000, total:  45000 },
      ],
      metaActual: 142000, metaMensual: 400000,
      vendedores: [
        { nombre: 'Carlos V.', monto: 72000 },
        { nombre: 'Laura S.',  monto: 45000 },
        { nombre: 'Pedro R.',  monto: 25000 },
      ],
    },
    comercial: {
      pipelineValor: 385000,
      grafica: [
        { sem: 'L', leads: 2, conversion: 40 }, { sem: 'M', leads: 3, conversion: 50 },
        { sem: 'X', leads: 1, conversion: 33 }, { sem: 'J', leads: 4, conversion: 62 },
        { sem: 'V', leads: 2, conversion: 55 }, { sem: 'S', leads: 1, conversion: 50 },
        { sem: 'H', leads: 2, conversion: 58 },
      ],
      leadsNuevos: 8, leadsCalificados: 5, proyectosGenerados: 4, tasaConversion: 58,
      funnel: [
        { etapa: 'Lead', count: 8 }, { etapa: 'Seguimiento', count: 6 },
        { etapa: 'Calificado', count: 5 }, { etapa: 'Proyecto', count: 4 },
      ],
    },
    operacion: {
      proyectosActivos: 14,
      grafica: [
        { sem: 'L', completados: 1, eficiencia: 78 }, { sem: 'M', completados: 2, eficiencia: 82 },
        { sem: 'X', completados: 1, eficiencia: 79 }, { sem: 'J', completados: 3, eficiencia: 85 },
        { sem: 'V', completados: 2, eficiencia: 83 }, { sem: 'S', completados: 0, eficiencia: 80 },
        { sem: 'H', completados: 2, eficiencia: 82 },
      ],
      flujo: { lead: 8, seguimiento: 6, proyecto: 14, ejecucion: 7, cierre: 5 },
      retrasados: 3, enTiempo: 11, eficiencia: 82,
    },
    inventario: {
      totalEquipos: 48,
      grafica: [
        { sem: 'L', movimientos: 3, temporales: 9 },  { sem: 'M', movimientos: 5, temporales: 10 },
        { sem: 'X', movimientos: 2, temporales: 8 },  { sem: 'J', movimientos: 7, temporales: 12 },
        { sem: 'V', movimientos: 4, temporales: 11 }, { sem: 'S', movimientos: 2, temporales: 9  },
        { sem: 'H', movimientos: 3, temporales: 11 },
      ],
      enAlmacen: 22, asignados: 15, temporalesActivos: 8, sinRecuperar: 3,
      equiposCriticos: [
        { nombre: 'Multímetro Fluke 117', responsable: 'Miguel G.', dias: 12 },
        { nombre: 'Taladro Dewalt 20V',   responsable: 'Omar L.',   dias: 8  },
        { nombre: 'Nivel láser Bosch',    responsable: 'Pedro R.',  dias: 5  },
      ],
    },
  },
  mes: {
    finanzas: {
      facturado: 312000, cobrado: 218000, porCobrar: 68000, vencido: 26000,
      grafica: [
        { sem: 'S1', ingresos: 62000,  meta: 100000 }, { sem: 'S2', ingresos: 88000, meta: 200000 },
        { sem: 'S3', ingresos: 91000,  meta: 300000 }, { sem: 'S4', ingresos: 71000, meta: 400000 },
      ],
      anticiposCobrados: { count: 9, monto: 186000 },
      finiquitosPendientes: { count: 7, monto: 189000 },
      proyectosFiniquito: [
        { nombre: 'Farma Plus – HVAC',    anticipo: 48000, finiquito: 52000, total: 100000 },
        { nombre: 'C. Com. Nexus',        anticipo: 32000, finiquito: 38000, total:  70000 },
        { nombre: 'Hospital Zona Norte',  anticipo: 56000, finiquito: 44000, total: 100000 },
      ],
      metaActual: 312000, metaMensual: 400000,
      vendedores: [
        { nombre: 'Carlos V.', monto: 162000 },
        { nombre: 'Laura S.',  monto:  98000 },
        { nombre: 'Pedro R.',  monto:  52000 },
      ],
    },
    comercial: {
      pipelineValor: 520000,
      grafica: [
        { sem: 'S1', leads: 8, conversion: 60 }, { sem: 'S2', leads: 7, conversion: 65 },
        { sem: 'S3', leads: 5, conversion: 68 }, { sem: 'S4', leads: 4, conversion: 65 },
      ],
      leadsNuevos: 24, leadsCalificados: 16, proyectosGenerados: 14, tasaConversion: 65,
      funnel: [
        { etapa: 'Lead', count: 24 }, { etapa: 'Seguimiento', count: 18 },
        { etapa: 'Calificado', count: 16 }, { etapa: 'Proyecto', count: 14 },
      ],
    },
    operacion: {
      proyectosActivos: 18,
      grafica: [
        { sem: 'S1', completados: 4, eficiencia: 84 }, { sem: 'S2', completados: 6, eficiencia: 87 },
        { sem: 'S3', completados: 5, eficiencia: 89 }, { sem: 'S4', completados: 3, eficiencia: 87 },
      ],
      flujo: { lead: 9, seguimiento: 6, proyecto: 18, ejecucion: 9, cierre: 12 },
      retrasados: 4, enTiempo: 14, eficiencia: 87,
    },
    inventario: {
      totalEquipos: 48,
      grafica: [
        { sem: 'S1', movimientos: 14, temporales: 9 }, { sem: 'S2', movimientos: 18, temporales: 11 },
        { sem: 'S3', movimientos: 12, temporales: 10 },{ sem: 'S4', movimientos: 10, temporales: 8  },
      ],
      enAlmacen: 22, asignados: 15, temporalesActivos: 8, sinRecuperar: 3,
      equiposCriticos: [
        { nombre: 'Multímetro Fluke 117', responsable: 'Miguel G.', dias: 12 },
        { nombre: 'Taladro Dewalt 20V',   responsable: 'Omar L.',   dias: 8  },
        { nombre: 'Nivel láser Bosch',    responsable: 'Pedro R.',  dias: 5  },
      ],
    },
  },
  'año': {
    finanzas: {
      facturado: 2840000, cobrado: 2180000, porCobrar: 480000, vencido: 180000,
      grafica: [
        { sem: 'Ene', ingresos: 180000,  meta: 400000  }, { sem: 'Feb', ingresos: 220000, meta: 800000  },
        { sem: 'Mar', ingresos: 310000,  meta: 1200000 }, { sem: 'Abr', ingresos: 290000, meta: 1600000 },
        { sem: 'May', ingresos: 340000,  meta: 2000000 }, { sem: 'Jun', ingresos: 380000, meta: 2400000 },
        { sem: 'Jul', ingresos: 420000,  meta: 2800000 }, { sem: 'Ago', ingresos: 350000, meta: 3200000 },
        { sem: 'Sep', ingresos: 410000,  meta: 3600000 }, { sem: 'Oct', ingresos: 390000, meta: 4000000 },
        { sem: 'Nov', ingresos: 360000,  meta: 4400000 }, { sem: 'Dic', ingresos: 670000, meta: 4800000 },
      ],
      anticiposCobrados: { count: 42, monto: 1240000 },
      finiquitosPendientes: { count: 11, monto: 480000 },
      proyectosFiniquito: [
        { nombre: 'Hospital Zona Norte',  anticipo: 160000, finiquito: 140000, total: 300000 },
        { nombre: 'Plaza Comercial Sur',  anticipo: 120000, finiquito: 100000, total: 220000 },
        { nombre: 'Farma Plus – HVAC',    anticipo:  48000, finiquito:  52000, total: 100000 },
      ],
      metaActual: 2840000, metaMensual: 4800000,
      vendedores: [
        { nombre: 'Carlos V.', monto: 1420000 },
        { nombre: 'Laura S.',  monto:   880000 },
        { nombre: 'Pedro R.',  monto:   540000 },
      ],
    },
    comercial: {
      pipelineValor: 1850000,
      grafica: [
        { sem: 'Ene', leads: 12, conversion: 58 }, { sem: 'Feb', leads: 15, conversion: 62 },
        { sem: 'Mar', leads: 18, conversion: 65 }, { sem: 'Abr', leads: 14, conversion: 64 },
        { sem: 'May', leads: 16, conversion: 68 }, { sem: 'Jun', leads: 20, conversion: 70 },
        { sem: 'Jul', leads: 18, conversion: 72 }, { sem: 'Ago', leads: 15, conversion: 69 },
        { sem: 'Sep', leads: 17, conversion: 71 }, { sem: 'Oct', leads: 16, conversion: 70 },
        { sem: 'Nov', leads: 14, conversion: 68 }, { sem: 'Dic', leads: 11, conversion: 71 },
      ],
      leadsNuevos: 186, leadsCalificados: 138, proyectosGenerados: 126, tasaConversion: 71,
      funnel: [
        { etapa: 'Lead', count: 186 }, { etapa: 'Seguimiento', count: 152 },
        { etapa: 'Calificado', count: 138 }, { etapa: 'Proyecto', count: 126 },
      ],
    },
    operacion: {
      proyectosActivos: 22,
      grafica: [
        { sem: 'Ene', completados: 8,  eficiencia: 82 }, { sem: 'Feb', completados: 10, eficiencia: 85 },
        { sem: 'Mar', completados: 12, eficiencia: 87 }, { sem: 'Abr', completados: 9,  eficiencia: 86 },
        { sem: 'May', completados: 11, eficiencia: 88 }, { sem: 'Jun', completados: 14, eficiencia: 90 },
        { sem: 'Jul', completados: 13, eficiencia: 91 }, { sem: 'Ago', completados: 10, eficiencia: 89 },
        { sem: 'Sep', completados: 12, eficiencia: 90 }, { sem: 'Oct', completados: 11, eficiencia: 91 },
        { sem: 'Nov', completados: 9,  eficiencia: 89 }, { sem: 'Dic', completados: 7,  eficiencia: 91 },
      ],
      flujo: { lead: 12, seguimiento: 8, proyecto: 22, ejecucion: 11, cierre: 48 },
      retrasados: 5, enTiempo: 17, eficiencia: 91,
    },
    inventario: {
      totalEquipos: 48,
      grafica: [
        { sem: 'Ene', movimientos: 42, temporales: 8  }, { sem: 'Feb', movimientos: 55, temporales: 10 },
        { sem: 'Mar', movimientos: 63, temporales: 12 }, { sem: 'Abr', movimientos: 48, temporales: 9  },
        { sem: 'May', movimientos: 52, temporales: 11 }, { sem: 'Jun', movimientos: 70, temporales: 14 },
        { sem: 'Jul', movimientos: 65, temporales: 13 }, { sem: 'Ago', movimientos: 44, temporales: 10 },
        { sem: 'Sep', movimientos: 58, temporales: 12 }, { sem: 'Oct', movimientos: 51, temporales: 11 },
        { sem: 'Nov', movimientos: 46, temporales: 9  }, { sem: 'Dic', movimientos: 38, temporales: 8  },
      ],
      enAlmacen: 22, asignados: 15, temporalesActivos: 8, sinRecuperar: 3,
      equiposCriticos: [
        { nombre: 'Multímetro Fluke 117', responsable: 'Miguel G.', dias: 12 },
        { nombre: 'Taladro Dewalt 20V',   responsable: 'Omar L.',   dias: 8  },
        { nombre: 'Nivel láser Bosch',    responsable: 'Pedro R.',  dias: 5  },
      ],
    },
  },
};

function formatPeso(n) {
  return '$' + n.toLocaleString('es-MX', OPC_DINERO);
}
function formatPesoCorto(n) {
  if (n >= 1000000) return '$' + (n / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
  // SIN-REDONDEO: OK (resumen abreviado "$12k" de un tablero, no es un importe)
  if (n >= 1000)    return '$' + Math.round(n / 1000) + 'k';
  return '$' + n.toLocaleString('es-MX', OPC_DINERO);
}

// ============================================
// COMPONENTES AUXILIARES
// ============================================
function AnimatedNumber({ target }) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    let start = 0;
    const step = Math.ceil(target / 20);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setVal(target); clearInterval(timer); }
      else setVal(start);
    }, 40);
    return () => clearInterval(timer);
  }, [target]);
  return <span>{val}</span>;
}

function useWindowWidth() {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handle = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handle);
    return () => window.removeEventListener('resize', handle);
  }, []);
  return width;
}

function TooltipCustom({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#0f1923', borderRadius: '10px', padding: '10px 14px', fontSize: '11px', fontFamily: "'Poppins', sans-serif", boxShadow: '0 6px 20px rgba(0,0,0,0.25)' }}>
      <div style={{ fontWeight: '700', marginBottom: '6px', color: '#fff', letterSpacing: '0.03em' }}>{label}</div>
      {payload.map((p, i) => {
        const isPercent = p.name.includes('%');
        const val = isPercent ? p.value + '%' : p.value >= 10000 ? formatPesoCorto(p.value) : p.value;
        return (
          <div key={i} style={{ color: p.color, marginBottom: '2px', display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
            <span style={{ opacity: 0.8 }}>{p.name}</span>
            <strong>{val}</strong>
          </div>
        );
      })}
    </div>
  );
}

function BarraProgreso({ valor, meta, colorForzado }) {
  const pct = Math.min(100, Math.round((valor / meta) * 100));
  const c = colorForzado || (pct >= 80 ? '#1D9E75' : pct >= 50 ? '#9a8300' : colores.rojo);
  return (
    <div>
      <div style={{ position: 'relative', height: '22px', borderRadius: '11px', background: '#e8e8e8', marginBottom: '6px' }}>
        <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${pct}%`, background: `linear-gradient(90deg, ${c}99, ${c})`, borderRadius: '11px', transition: 'width 0.6s ease' }} />
        {[25, 50, 75].map(m => (
          <div key={m} style={{ position: 'absolute', left: `${m}%`, top: 0, bottom: 0, width: '2px', background: 'rgba(255,255,255,0.7)', zIndex: 1 }} />
        ))}
        {pct > 12 && (
          <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${pct}%`, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: '7px', zIndex: 2 }}>
            <span style={{ fontSize: '10px', fontWeight: '700', color: '#fff' }}>{pct}%</span>
          </div>
        )}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        {[0, 25, 50, 75, 100].map(m => (
          <span key={m} style={{ fontSize: '9px', color: colores.grisClaro, fontFamily: "'Poppins', sans-serif" }}>{m}%</span>
        ))}
      </div>
    </div>
  );
}

function BtnExportar() {
  return (
    <button style={{ background: 'transparent', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '6px 12px', fontSize: '11px', color: colores.grisClaro, cursor: 'pointer', fontFamily: "'Poppins', sans-serif", fontWeight: '500', display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
      ↓ Exportar
    </button>
  );
}

// ============================================
// PLACEHOLDER para módulos no construidos
// ============================================
function PlaceholderModulo({ seccion, sub }) {
  const sec = secciones.find(s => s.id === seccion);
  const color = sec ? sec.color : colores.principal;
  const clave = seccion + '.' + sub.id;
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
      <div style={{ textAlign: 'center', maxWidth: '420px' }}>
        <div style={{ width: '80px', height: '80px', borderRadius: '24px', background: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px', fontSize: '36px' }}>
          <IconoSeccion item={sub || sec} size={36} />
        </div>
        <div style={{ fontSize: '22px', fontWeight: '700', color: colores.gris, fontFamily: "'Poppins', sans-serif", marginBottom: '8px' }}>
          {sub.label}
        </div>
        <div style={{ fontSize: '14px', color: colores.grisClaro, fontFamily: "'Poppins', sans-serif", lineHeight: 1.6, marginBottom: '20px' }}>
          Este módulo estará disponible próximamente.
        </div>
        <span style={{ display: 'inline-block', padding: '6px 20px', borderRadius: '20px', background: `${color}18`, color: color, fontSize: '12px', fontWeight: '700', fontFamily: "'Poppins', sans-serif", letterSpacing: '0.05em' }}>
          Próximamente
        </span>
      </div>
    </div>
  );
}

// ============================================
// COMPONENTE: Dashboard ejecutivo con 4 pestañas
// ============================================
function DashboardPanel({ esCelular, cambiarPagina }) {
  const [periodo, setPeriodo] = useState('mes');
  const [tab, setTab]         = useState('finanzas');
  const d = datosPorPeriodo[periodo];

  const TABS = [
    { key: 'finanzas',   label: 'Finanzas',   color: '#1D9E75'         },
    { key: 'comercial',  label: 'Comercial',  color: colores.amarillo  },
    { key: 'operacion',  label: 'Operación',  color: colores.principal },
    { key: 'inventario', label: 'Inventario', color: '#a8d96a'         },
  ];

  const PP  = "'Poppins', sans-serif";
  const CARD = { background: '#fafaf8', borderRadius: '16px', padding: esCelular ? '16px' : '24px', boxShadow: '0 1px 8px rgba(0,0,0,0.06)' };
  const LBL  = { fontSize: '11px', fontWeight: '400', color: colores.grisClaro, letterSpacing: '0.06em', textTransform: 'uppercase', fontFamily: PP };
  const BIG  = { fontSize: esCelular ? '28px' : '38px', fontWeight: '800', color: colores.gris, letterSpacing: '-0.02em', lineHeight: 1, fontFamily: PP, marginTop: '6px' };
  const GAP  = { display: 'flex', flexDirection: 'column', gap: '20px' };

  const navegar = (destino) => { if (cambiarPagina) cambiarPagina(destino); };

  const renderFinanzas = () => {
    const fin = d.finanzas;
    const maxVend = Math.max(...fin.vendedores.map(v => v.monto));
    return (
      <div style={GAP}>
        <div style={CARD}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={LBL}>Total facturado</div>
              <div style={{ ...BIG, cursor: 'pointer', color: '#1D9E75' }} onClick={() => navegar('operaciones.expedientes-todos')}>{formatPeso(fin.facturado)}</div>
            </div>
            <BtnExportar />
          </div>
          <div style={{ height: esCelular ? '160px' : '210px', marginTop: '20px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={fin.grafica} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis dataKey="sem" tick={{ fontSize: 10, fill: colores.grisClaro, fontFamily: 'Poppins' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: colores.grisClaro, fontFamily: 'Poppins' }} axisLine={false} tickLine={false} tickFormatter={v => v >= 1000 ? (v / 1000) + 'k' : v} width={38} />
                <Tooltip content={<TooltipCustom />} />
                <Bar dataKey="ingresos" name="Ingresos" fill={colores.principal} radius={[5, 5, 0, 0]} fillOpacity={0.9} />
                <Line type="monotone" dataKey="meta" name="Meta acum." stroke="#1D9E75" strokeWidth={2.5} dot={false} strokeDasharray="6 3" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <div style={{ display: 'flex', gap: '14px', marginTop: '8px', justifyContent: 'flex-end' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '10px', color: colores.grisClaro, fontFamily: PP }}><span style={{ width: '10px', height: '10px', background: colores.principal, borderRadius: '2px', display: 'inline-block' }} /> Ingresos</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '10px', color: colores.grisClaro, fontFamily: PP }}><span style={{ width: '14px', height: '2px', background: '#1D9E75', display: 'inline-block', borderRadius: '1px' }} /> Meta</span>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: esCelular ? '1fr' : '1.4fr 1fr 1fr', gap: '16px' }}>
          <div style={{ ...CARD, background: '#f0faf6', cursor: 'pointer' }} onClick={() => navegar('operaciones.expedientes-todos')}>
            <div style={{ ...LBL, color: '#1D9E75' }}>Cobrado</div>
            <div style={{ ...BIG, fontSize: esCelular ? '32px' : '44px', color: '#1D9E75' }}>{formatPesoCorto(fin.cobrado)}</div>
            <div style={{ fontSize: '11px', color: '#1D9E75', marginTop: '5px', fontFamily: PP }}>{Math.round((fin.cobrado / fin.facturado) * 100)}% del total facturado</div>
          </div>
          <div style={{ ...CARD, background: '#fffbe6', cursor: 'pointer' }} onClick={() => navegar('operaciones.expedientes-todos')}>
            <div style={{ ...LBL, color: '#9a8300' }}>Por cobrar</div>
            <div style={{ ...BIG, fontSize: esCelular ? '28px' : '34px', color: '#9a8300' }}>{formatPesoCorto(fin.porCobrar)}</div>
            <div style={{ fontSize: '11px', color: '#9a8300', marginTop: '5px', fontFamily: PP }}>{Math.round((fin.porCobrar / fin.facturado) * 100)}% pendiente</div>
          </div>
          <div style={{ ...CARD, background: '#fff0f0', cursor: 'pointer' }} onClick={() => navegar('operaciones.expedientes-todos')}>
            <div style={{ ...LBL, color: colores.rojo }}>Vencido</div>
            <div style={{ ...BIG, fontSize: esCelular ? '28px' : '34px', color: colores.rojo }}>{formatPesoCorto(fin.vencido)}</div>
            <div style={{ fontSize: '11px', color: colores.rojo, marginTop: '5px', fontFamily: PP }}>{Math.round((fin.vencido / fin.facturado) * 100)}% atención urgente</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: esCelular ? '1fr' : '1fr 1fr', gap: '20px' }}>
          <div style={CARD}>
            <div style={LBL}>Anticipos cobrados</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginTop: '6px' }}>
              <div style={{ ...BIG, color: '#1D9E75', fontSize: esCelular ? '28px' : '34px' }}>{formatPesoCorto(fin.anticiposCobrados.monto)}</div>
              <span style={{ fontSize: '12px', color: colores.grisClaro, fontFamily: PP }}>{fin.anticiposCobrados.count} proyectos</span>
            </div>
            <div style={{ fontSize: '11px', color: '#1D9E75', marginTop: '5px', fontFamily: PP }}>Estructura 80 / 20 vigente</div>
          </div>
          <div style={{ ...CARD, borderLeft: `3px solid ${colores.amarillo}`, cursor: 'pointer' }} onClick={() => navegar('operaciones.expedientes-todos')}>
            <div style={{ ...LBL, color: '#9a8300' }}>Finiquitos pendientes</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginTop: '6px' }}>
              <div style={{ ...BIG, color: '#9a8300', fontSize: esCelular ? '28px' : '34px' }}>{formatPesoCorto(fin.finiquitosPendientes.monto)}</div>
              <span style={{ fontSize: '12px', color: '#9a8300', fontFamily: PP }}>{fin.finiquitosPendientes.count} proyectos</span>
            </div>
            <div style={{ fontSize: '11px', color: '#9a8300', marginTop: '5px', fontFamily: PP }}>Ver en Proyectos →</div>
          </div>
        </div>

        <div style={CARD}>
          <div style={{ ...LBL, marginBottom: '18px' }}>Proyectos con mayor finiquito pendiente</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
            {fin.proyectosFiniquito.map((p, i) => {
              const pctAnticipo = Math.round((p.anticipo / p.total) * 100);
              return (
                <div key={i}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '7px' }}>
                    <span style={{ fontSize: '13px', fontWeight: '600', color: colores.gris, fontFamily: PP }}>{p.nombre}</span>
                    <div style={{ display: 'flex', gap: '14px', fontSize: '11px', fontFamily: PP }}>
                      <span style={{ color: '#1D9E75' }}>↑ {formatPesoCorto(p.anticipo)}</span>
                      <span style={{ color: '#9a8300' }}>↓ {formatPesoCorto(p.finiquito)}</span>
                    </div>
                  </div>
                  <div style={{ position: 'relative', height: '14px', borderRadius: '7px', overflow: 'hidden', background: '#e8e8e8' }}>
                    <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${pctAnticipo}%`, background: '#1D9E75' }} />
                    <div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: `${100 - pctAnticipo}%`, background: `${colores.amarillo}bb` }} />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
                    <span style={{ fontSize: '9px', color: '#1D9E75', fontFamily: PP }}>Anticipo {pctAnticipo}%</span>
                    <span style={{ fontSize: '9px', color: '#9a8300', fontFamily: PP }}>Finiquito {100 - pctAnticipo}%</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div style={CARD}>
          <div style={LBL}>Meta del mes</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', margin: '8px 0 14px' }}>
            <div style={{ ...BIG, color: fin.metaActual / fin.metaMensual >= 0.8 ? '#1D9E75' : fin.metaActual / fin.metaMensual >= 0.5 ? '#9a8300' : colores.rojo }}>{formatPeso(fin.metaActual)}</div>
            <span style={{ fontSize: '13px', color: colores.grisClaro, fontFamily: PP }}>/ {formatPeso(fin.metaMensual)}</span>
          </div>
          <BarraProgreso valor={fin.metaActual} meta={fin.metaMensual} />
          <div style={{ ...LBL, marginTop: '20px', marginBottom: '12px' }}>Ranking vendedores</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {fin.vendedores.map((v, i) => {
              const pct = Math.round((v.monto / maxVend) * 100);
              const rc  = [colores.principal, '#5cd4b0', colores.claro];
              return (
                <div key={i}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                      <div style={{ width: '18px', height: '18px', borderRadius: '50%', background: rc[i], display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', fontWeight: '800', color: '#fff', flexShrink: 0, fontFamily: PP }}>{i + 1}</div>
                      <span style={{ fontSize: '12px', color: colores.gris, fontWeight: '500', fontFamily: PP }}>{v.nombre}</span>
                    </div>
                    <span style={{ fontSize: '12px', fontWeight: '700', color: colores.gris, fontFamily: PP }}>{formatPesoCorto(v.monto)}</span>
                  </div>
                  <div style={{ height: '6px', background: '#e8e8e8', borderRadius: '3px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: rc[i], borderRadius: '3px', transition: 'width 0.5s ease' }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderComercial = () => {
    const com = d.comercial;
    const fMax = com.funnel[0].count;
    const funnelColors = [colores.amarillo, '#5cd4b0', colores.principal, '#a8d96a'];
    return (
      <div style={GAP}>
        <div style={CARD}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={LBL}>Pipeline total</div>
              <div style={{ ...BIG, cursor: 'pointer' }} onClick={() => navegar('comercial.prospectos')}>{formatPeso(com.pipelineValor)}</div>
            </div>
            <BtnExportar />
          </div>
          <div style={{ height: esCelular ? '160px' : '210px', marginTop: '20px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={com.grafica} margin={{ top: 4, right: 32, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis dataKey="sem" tick={{ fontSize: 10, fill: colores.grisClaro, fontFamily: 'Poppins' }} axisLine={false} tickLine={false} />
                <YAxis yAxisId="left" tick={{ fontSize: 10, fill: colores.grisClaro, fontFamily: 'Poppins' }} axisLine={false} tickLine={false} width={28} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fill: colores.grisClaro, fontFamily: 'Poppins' }} axisLine={false} tickLine={false} tickFormatter={v => v + '%'} domain={[0, 100]} width={36} />
                <Tooltip content={<TooltipCustom />} />
                <Bar yAxisId="left" dataKey="leads" name="Leads" fill={colores.amarillo} radius={[5, 5, 0, 0]} fillOpacity={0.9} />
                <Line yAxisId="right" type="monotone" dataKey="conversion" name="Conversión %" stroke={colores.principal} strokeWidth={2.5} dot={{ fill: colores.principal, r: 3 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: esCelular ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: '16px' }}>
          {[
            { label: 'Leads nuevos',       val: com.leadsNuevos,         bg: '#fffbe6', color: '#7a6e00',    nav: 'comercial.prospectos' },
            { label: 'Leads calificados',   val: com.leadsCalificados,    bg: '#eef5ff', color: colores.oscuro, nav: 'comercial.prospectos' },
            { label: 'Proyectos generados', val: com.proyectosGenerados,  bg: '#f0faf6', color: '#1D9E75',    nav: 'operaciones.expedientes-todos' },
            { label: 'Tasa conversión',     val: com.tasaConversion,      bg: '#fff5ee', color: '#c45e00',    nav: 'comercial.prospectos', sufijo: '%' },
          ].map((m, i) => (
            <div key={i} style={{ ...CARD, background: m.bg, cursor: 'pointer' }} onClick={() => navegar(m.nav)}>
              <div style={{ ...LBL, color: m.color }}>{m.label}</div>
              <div style={{ fontSize: esCelular ? '26px' : '34px', fontWeight: '800', color: m.color, marginTop: '6px', lineHeight: 1, fontFamily: PP }}>
                <AnimatedNumber target={m.val} />{m.sufijo || ''}
              </div>
            </div>
          ))}
        </div>

        <div style={CARD}>
          <div style={{ ...LBL, marginBottom: '20px' }}>Embudo de conversión</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {com.funnel.map((fItem, i, arr) => {
              const wPct    = Math.round((fItem.count / fMax) * 100);
              const convPct = i > 0 ? Math.round((fItem.count / arr[i - 1].count) * 100) : null;
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '10px', color: colores.grisClaro, width: '82px', flexShrink: 0, fontFamily: PP }}>{fItem.etapa}</span>
                  <div style={{ flex: 1, height: '34px', position: 'relative' }}>
                    <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${wPct}%`, background: funnelColors[i], borderRadius: '7px', display: 'flex', alignItems: 'center', paddingLeft: '10px', transition: 'width 0.5s ease' }}>
                      <span style={{ fontSize: '14px', fontWeight: '800', color: i === 0 ? colores.gris : '#fff', fontFamily: PP }}>{fItem.count}</span>
                    </div>
                  </div>
                  {convPct !== null && (
                    <span style={{ fontSize: '11px', fontWeight: '700', color: convPct >= 80 ? '#1D9E75' : convPct >= 60 ? '#9a8300' : colores.rojo, width: '38px', flexShrink: 0, fontFamily: PP }}>{convPct}%</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderOperacion = () => {
    const op = d.operacion;
    const flujoGradiente = [
      { etapa: 'Lead',        count: op.flujo.lead,        color: colores.claro, textColor: colores.gris },
      { etapa: 'Seguimiento', count: op.flujo.seguimiento, color: '#7aacd4',     textColor: '#fff' },
      { etapa: 'Proyecto',    count: op.flujo.proyecto,    color: '#5489c4',     textColor: '#fff' },
      { etapa: 'Ejecución',   count: op.flujo.ejecucion,   color: '#3c70b0',     textColor: '#fff' },
      { etapa: 'Cierre',      count: op.flujo.cierre,      color: colores.oscuro, textColor: '#fff' },
    ];
    return (
      <div style={GAP}>
        <div style={CARD}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={LBL}>Proyectos activos</div>
              <div style={{ ...BIG, cursor: 'pointer' }} onClick={() => navegar('operaciones.expedientes-todos')}><AnimatedNumber target={op.proyectosActivos} /></div>
            </div>
            <BtnExportar />
          </div>
          <div style={{ height: esCelular ? '160px' : '210px', marginTop: '20px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={op.grafica} margin={{ top: 4, right: 32, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis dataKey="sem" tick={{ fontSize: 10, fill: colores.grisClaro, fontFamily: 'Poppins' }} axisLine={false} tickLine={false} />
                <YAxis yAxisId="left" tick={{ fontSize: 10, fill: colores.grisClaro, fontFamily: 'Poppins' }} axisLine={false} tickLine={false} width={28} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fill: colores.grisClaro, fontFamily: 'Poppins' }} axisLine={false} tickLine={false} tickFormatter={v => v + '%'} domain={[60, 100]} width={36} />
                <Tooltip content={<TooltipCustom />} />
                <Bar yAxisId="left" dataKey="completados" name="Completados" fill={colores.principal} radius={[5, 5, 0, 0]} fillOpacity={0.9} />
                <Line yAxisId="right" type="monotone" dataKey="eficiencia" name="Eficiencia %" stroke="#1D9E75" strokeWidth={2.5} dot={{ fill: '#1D9E75', r: 3 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div style={CARD}>
          <div style={{ ...LBL, marginBottom: '16px' }}>Flujo operativo</div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {flujoGradiente.map((f, i, arr) => (
              <React.Fragment key={i}>
                <div style={{ flex: 1, textAlign: 'center', cursor: 'pointer' }} onClick={() => navegar('operaciones.expedientes-todos')}>
                  <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: f.color, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 5px', fontSize: '15px', fontWeight: '800', color: f.textColor, boxShadow: `0 4px 12px ${f.color}50`, fontFamily: PP }}>
                    <AnimatedNumber target={f.count} />
                  </div>
                  <div style={{ fontSize: '9px', color: colores.grisClaro, fontFamily: PP, whiteSpace: 'nowrap' }}>{f.etapa}</div>
                </div>
                {i < arr.length - 1 && <div style={{ color: '#ccc', fontSize: '13px', flexShrink: 0, marginBottom: '14px' }}>›</div>}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
          {[
            { label: 'Retrasados', val: op.retrasados, bg: '#fff0f0', color: colores.rojo },
            { label: 'En tiempo',  val: op.enTiempo,   bg: '#f0faf6', color: '#1D9E75' },
            { label: 'Eficiencia', val: op.eficiencia, bg: '#eef5ff', color: colores.principal, sufijo: '%' },
          ].map((m, i) => (
            <div key={i} style={{ ...CARD, background: m.bg, cursor: 'pointer', textAlign: 'center' }} onClick={() => navegar('operaciones.expedientes-todos')}>
              <div style={{ ...LBL, color: m.color, textAlign: 'center' }}>{m.label}</div>
              <div style={{ fontSize: esCelular ? '28px' : '38px', fontWeight: '800', color: m.color, marginTop: '6px', fontFamily: PP, lineHeight: 1 }}>
                <AnimatedNumber target={m.val} />{m.sufijo || ''}
              </div>
            </div>
          ))}
        </div>

        <div style={CARD}>
          <div style={{ ...LBL, marginBottom: '16px' }}>Estado del equipo</div>
          <div style={{ display: 'grid', gridTemplateColumns: esCelular ? 'repeat(2, 1fr)' : 'repeat(5, 1fr)', gap: '12px' }}>
            {equipo.map((e, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px', borderRadius: '12px', background: '#f3f4f4', cursor: 'pointer' }} onClick={() => navegar('configuracion.usuarios')}>
                <div style={{ width: '34px', height: '34px', borderRadius: '50%', background: e.activo ? colores.principal : '#d0d0d0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: '700', color: '#fff', flexShrink: 0, fontFamily: PP }}>{e.nombre[0]}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '11px', fontWeight: '600', color: colores.gris, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontFamily: PP }}>{e.nombre}</div>
                  <div style={{ fontSize: '9px', color: colores.grisClaro, fontFamily: PP }}>{e.rol}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px' }}>
                    <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: e.activo ? '#1D9E75' : '#d0d0d0', flexShrink: 0 }} />
                    <span style={{ fontSize: '9px', color: e.activo ? '#1D9E75' : '#d0d0d0', fontFamily: PP }}>{e.activo ? 'Activo' : 'Inactivo'}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderInventario = () => {
    const inv = d.inventario;
    return (
      <div style={GAP}>
        <div style={CARD}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={LBL}>Total equipos</div>
              <div style={{ ...BIG, cursor: 'pointer' }} onClick={() => navegar('inventario.almacen')}><AnimatedNumber target={inv.totalEquipos} /></div>
            </div>
            <BtnExportar />
          </div>
          <div style={{ height: esCelular ? '160px' : '210px', marginTop: '20px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={inv.grafica} margin={{ top: 4, right: 32, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis dataKey="sem" tick={{ fontSize: 10, fill: colores.grisClaro, fontFamily: 'Poppins' }} axisLine={false} tickLine={false} />
                <YAxis yAxisId="left" tick={{ fontSize: 10, fill: colores.grisClaro, fontFamily: 'Poppins' }} axisLine={false} tickLine={false} width={28} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fill: colores.grisClaro, fontFamily: 'Poppins' }} axisLine={false} tickLine={false} width={28} />
                <Tooltip content={<TooltipCustom />} />
                <Bar yAxisId="left" dataKey="movimientos" name="Movimientos" fill={colores.principal} radius={[5, 5, 0, 0]} fillOpacity={0.9} />
                <Line yAxisId="right" type="monotone" dataKey="temporales" name="Temporales" stroke={colores.amarillo} strokeWidth={2.5} dot={{ fill: colores.amarillo, r: 3 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: esCelular ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: '16px' }}>
          {[
            { label: 'En almacén',        val: inv.enAlmacen,         bg: '#f0faf6', color: '#1D9E75' },
            { label: 'Asignados',          val: inv.asignados,          bg: '#eef5ff', color: colores.principal },
            { label: 'Temporales activos', val: inv.temporalesActivos, bg: '#fffbe6', color: '#9a8300' },
            { label: 'Sin recuperar',      val: inv.sinRecuperar,       bg: '#fff0f0', color: colores.rojo },
          ].map((m, i) => (
            <div key={i} style={{ ...CARD, background: m.bg, cursor: 'pointer', textAlign: 'center' }} onClick={() => navegar('inventario.almacen')}>
              <div style={{ ...LBL, color: m.color, textAlign: 'center' }}>{m.label}</div>
              <div style={{ fontSize: esCelular ? '28px' : '38px', fontWeight: '800', color: m.color, marginTop: '6px', fontFamily: PP, lineHeight: 1 }}><AnimatedNumber target={m.val} /></div>
            </div>
          ))}
        </div>

        <div style={CARD}>
          <div style={{ ...LBL, marginBottom: '18px' }}>Equipos temporales sin recuperar (críticos)</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {inv.equiposCriticos.map((eq, i) => {
              const urgencia     = Math.min(100, Math.round((eq.dias / 14) * 100));
              const colorUrgencia = eq.dias >= 10 ? colores.rojo : eq.dias >= 7 ? '#9a8300' : '#1D9E75';
              return (
                <div key={i}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '7px' }}>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: '600', color: colores.gris, fontFamily: PP }}>{eq.nombre}</div>
                      <div style={{ fontSize: '10px', color: colores.grisClaro, fontFamily: PP, marginTop: '2px' }}>Resp: {eq.responsable}</div>
                    </div>
                    <div style={{ fontSize: '24px', fontWeight: '800', color: colorUrgencia, fontFamily: PP, lineHeight: 1 }}>{eq.dias}d</div>
                  </div>
                  <div style={{ position: 'relative', height: '10px', borderRadius: '5px', background: '#e8e8e8' }}>
                    <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${urgencia}%`, background: colorUrgencia, borderRadius: '5px', transition: 'width 0.5s ease' }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ fontFamily: PP }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
        {[
          { key: 'hoy', label: 'Hoy' }, { key: 'semana', label: 'Semana' },
          { key: 'mes', label: 'Mes' }, { key: 'año', label: 'Año' },
        ].map(p => (
          <button key={p.key} onClick={() => setPeriodo(p.key)} style={{
            padding: '6px 18px', borderRadius: '20px', border: 'none', cursor: 'pointer',
            fontSize: '12px', fontWeight: '600', transition: 'all 0.15s', fontFamily: PP,
            background: periodo === p.key ? colores.principal : '#ffffff',
            color: periodo === p.key ? '#ffffff' : colores.gris,
            boxShadow: periodo === p.key ? '0 2px 8px rgba(92,154,219,0.35)' : '0 1px 3px rgba(0,0,0,0.08)',
          }}>{p.label}</button>
        ))}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: esCelular ? '0' : 'auto' }}>
          <input type="date" style={{ padding: '5px 8px', borderRadius: '8px', border: '1px solid #e0e0e0', fontSize: '11px', color: colores.gris, background: '#fff', fontFamily: PP }} />
          <span style={{ fontSize: '11px', color: colores.grisClaro }}>—</span>
          <input type="date" style={{ padding: '5px 8px', borderRadius: '8px', border: '1px solid #e0e0e0', fontSize: '11px', color: colores.gris, background: '#fff', fontFamily: PP }} />
        </div>
      </div>

      <div style={{ overflowX: 'auto', marginBottom: '24px', borderBottom: '2px solid #ebebeb' }}>
        <div style={{ display: 'flex', minWidth: 'max-content' }}>
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} style={{
              padding: '10px 22px', background: 'transparent', border: 'none',
              borderBottom: tab === t.key ? `3px solid ${t.color}` : '3px solid transparent',
              marginBottom: '-2px', cursor: 'pointer', fontSize: '13px',
              fontWeight: tab === t.key ? '700' : '400',
              color: tab === t.key ? colores.gris : colores.grisClaro,
              fontFamily: PP, transition: 'all 0.15s', whiteSpace: 'nowrap',
            }}>{t.label}</button>
          ))}
        </div>
      </div>

      {tab === 'finanzas'   && renderFinanzas()}
      {tab === 'comercial'  && renderComercial()}
      {tab === 'operacion'  && renderOperacion()}
      {tab === 'inventario' && renderInventario()}
    </div>
  );
}

// ============================================
// COMPONENTE PRINCIPAL: Dashboard
// ============================================
function Dashboard({ usuario, nombreUsuario, rolUsuario, usuarioObj, setLogueado, onIrAExpo }) {
  // Persistir que estamos en Beep OS
  useEffect(() => { storage.set(STORAGE_KEYS.CURRENT_APP, 'beep'); }, []);

  const [activoRaw, setActivoRaw] = useState(() => {
    const g = storage.getRaw(STORAGE_KEYS.DASHBOARD_ACTIVE_TAB);
    return g && typeof g === 'string' && g.includes('.') ? g : 'mitrabajo.midia';
  });
  const [tabAgenda, setTabAgenda] = useState('notas');
  const [canalActivoId, setCanalActivoId] = useState(null);
  // CO-2: carrito_id para el previsualizador de cotizacion (pasado via setActivo extra / evento).
  const [carritoPreviewId, setCarritoPreviewId] = useState(null);
  // CO-3.D: id de cotizacion para el previsualizador en modo EDITAR.
  const [cotizacionEditId, setCotizacionEditId] = useState(null);
  // ROUND-TRIP expediente: { cotizacionId, folio } de la cotizacion que esta
  // eligiendo expediente en la pagina de Expedientes (o null si no aplica).
  const [selExpCot, setSelExpCot] = useState(null);
  // CO-LINK-EXPEDIENTE: id de expediente cuya ficha hay que abrir al navegar a Expedientes
  // (disparado por "Ir al expediente" del editor). Expedientes lo consume y lo limpia.
  const [expedienteAbrirId, setExpedienteAbrirId] = useState(null);
  // BANNER DE RETORNO: contexto para volver al calendario (o de donde se abrio el expediente).
  const [retornoCal, setRetornoCal] = useState(null); // { label } | null
  const activo = activoRaw;
  // El banner de retorno solo vive mientras se ve un expediente; al navegar a otra cosa, se limpia.
  useEffect(() => {
    if (activo !== 'operaciones.expedientes-todos' && activo !== 'mitrabajo.mi-expediente') setRetornoCal(null);
  }, [activo]);
  const setActivo = (ruta, extra) => {
    if (ruta === 'mitrabajo.agentes-chat' && extra) setCanalActivoId(extra);
    if (ruta === 'cotizaciones.previsualizador' && extra != null) setCarritoPreviewId(extra);
    setActivoRaw(ruta);
    storage.set(STORAGE_KEYS.DASHBOARD_ACTIVE_TAB, ruta);
  };

  // CO-2 / CO-3.D: el carrito o el listado disparan estos eventos para navegar al
  // previsualizador, sin acoplar CarritoFlotante/CotizacionesLista a Dashboard.
  useEffect(() => {
    const irAPreview = () => {
      setActivoRaw('cotizaciones.previsualizador');
      storage.set(STORAGE_KEYS.DASHBOARD_ACTIVE_TAB, 'cotizaciones.previsualizador');
    };
    const onIr = (e) => {                 // nueva cotizacion desde el carrito
      const cid = e?.detail?.carritoId;
      setCotizacionEditId(null);
      if (cid != null) setCarritoPreviewId(cid);
      irAPreview();
    };
    const onEditar = (e) => {             // editar una cotizacion existente
      const id = e?.detail?.cotizacionId;
      if (id == null) return;
      setCarritoPreviewId(null);
      setCotizacionEditId(id);
      irAPreview();
    };
    // ROUND-TRIP: el editor ya guardo y poblo el carrito -> solo navegamos a La Tienda.
    const onAgregarEnTienda = () => {
      setActivoRaw('cotizaciones.tienda');
      storage.set(STORAGE_KEYS.DASHBOARD_ACTIVE_TAB, 'cotizaciones.tienda');
    };
    // ROUND-TRIP: volver de La Tienda al editor en modo "regreso" (cabecera de la
    // cotizacion + lineas del carrito). Ambos ids presentes -> el editor combina.
    const onVolverCotizacion = (e) => {
      const cotId = e?.detail?.cotizacionId;
      const carId = e?.detail?.carritoId;
      if (cotId == null) return;
      setCarritoPreviewId(carId != null ? carId : null);
      setCotizacionEditId(cotId);
      irAPreview();
    };
    // ROUND-TRIP expediente: el editor ya guardo -> guardamos para que cotizacion se
    // elige y navegamos a la pagina completa de Expedientes en modo seleccion.
    const onElegirExpediente = (e) => {
      const cotId = e?.detail?.cotizacionId;
      if (cotId == null) return;
      setSelExpCot({ cotizacionId: cotId, folio: e?.detail?.folio || '' });
      setActivoRaw('operaciones.expedientes-todos');
      storage.set(STORAGE_KEYS.DASHBOARD_ACTIVE_TAB, 'operaciones.expedientes-todos');
    };
    // CO-LINK-EXPEDIENTE: "Ir al expediente" desde el editor -> navega a Expedientes y
    // abre la ficha de ESE expediente por id (via prop aperturaInicialId de Expedientes).
    const onAbrirExpediente = (e) => {
      const id = e?.detail?.expedienteId;
      if (id == null) return;
      setExpedienteAbrirId(id);
      setRetornoCal(e?.detail?.retorno || null); // si vino del calendario, guarda el contexto de retorno
      // CALENDARIO-E1: quien puede ver el modulo Expedientes va a la lista global
      // (comportamiento normal). El tecnico restringido (solo Mi Trabajo) va a la
      // ruta virtual que muestra SOLO la ficha de su expediente asignado.
      const destino = (accesoTotal || puedeVer('operaciones'))
        ? 'operaciones.expedientes-todos'
        : 'mitrabajo.mi-expediente';
      setActivoRaw(destino);
      storage.set(STORAGE_KEYS.DASHBOARD_ACTIVE_TAB, destino);
    };
    // MOTOR DE ORIGEN F3.B: navegar a un tab del Dashboard (La Tienda/Colecciones/Listas)
    // al entrar en "modo agregar a cotizacion". Solo navegacion (el modo vive en storage).
    const onIrTab = (e) => {
      const tab = e?.detail?.tab;
      if (!tab) return;
      setActivoRaw(tab);
      storage.set(STORAGE_KEYS.DASHBOARD_ACTIVE_TAB, tab);
    };
    window.addEventListener('beep-ir-tab', onIrTab);
    window.addEventListener('beep-ir-cotizacion', onIr);
    window.addEventListener('beep-editar-cotizacion', onEditar);
    window.addEventListener('beep-agregar-en-tienda', onAgregarEnTienda);
    window.addEventListener('beep-volver-cotizacion', onVolverCotizacion);
    window.addEventListener('beep-elegir-expediente', onElegirExpediente);
    window.addEventListener('beep-abrir-expediente', onAbrirExpediente);
    // PORTADA-EXP (QR): deep-link ?exp=<id> — el QR impreso en la caratula abre
    // el expediente al cargar la app (misma ruta que beep-abrir-expediente).
    // Se limpia el parametro de la URL para no re-abrirlo en cada recarga.
    try {
      const qs = new URLSearchParams(window.location.search);
      const expQR = parseInt(qs.get('exp') || '', 10);
      if (expQR > 0) {
        onAbrirExpediente({ detail: { expedienteId: expQR } });
        qs.delete('exp');
        const resto = qs.toString();
        window.history.replaceState({}, '', window.location.pathname + (resto ? `?${resto}` : ''));
      }
    } catch { /* deep-link es best-effort */ }
    return () => {
      window.removeEventListener('beep-elegir-expediente', onElegirExpediente);
      window.removeEventListener('beep-abrir-expediente', onAbrirExpediente);
      window.removeEventListener('beep-ir-tab', onIrTab);
      window.removeEventListener('beep-ir-cotizacion', onIr);
      window.removeEventListener('beep-editar-cotizacion', onEditar);
      window.removeEventListener('beep-agregar-en-tienda', onAgregarEnTienda);
      window.removeEventListener('beep-volver-cotizacion', onVolverCotizacion);
    };
  }, []);

  const navegarConTab = (ruta, tab) => {
    if (tab) setTabAgenda(tab);
    setActivo(ruta);
  };

  // ROUND-TRIP expediente: al elegir un expediente en la pagina de Expedientes,
  // se re-vincula a la cotizacion (PATCH) y se reabre el editor (recargara ya
  // vinculada). Cancelar reabre el editor SIN cambiar nada. Definidos en el cuerpo
  // (no en el useEffect) para que capturen el selExpCot actual al renderizar.
  const onElegirExpediente = (exp) => {
    const sel = selExpCot;
    if (!sel) return;
    fetch(`${API_BASE_CLI}/api/cotizaciones/${sel.cotizacionId}/expediente`, {
      method: 'PATCH',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ oportunidad_id: exp?.id ?? null }),
    }).catch(() => {}).finally(() => {
      setSelExpCot(null);
      window.dispatchEvent(new CustomEvent('beep-editar-cotizacion', { detail: { cotizacionId: sel.cotizacionId } }));
    });
  };
  const onCancelarExpediente = () => {
    const sel = selExpCot;
    setSelExpCot(null);
    if (sel) window.dispatchEvent(new CustomEvent('beep-editar-cotizacion', { detail: { cotizacionId: sel.cotizacionId } }));
  };
  const [seccionAbierta, setSeccionAbierta] = useState('inicio');
  const [menuAbierto, setMenuAbierto] = useState(false);
  const [menuPerfilTopbar, setMenuPerfilTopbar] = useState(false);
  const [menuPerfilSidebar, setMenuPerfilSidebar] = useState(false);
  const [animando, setAnimando] = useState(false);
  const [tabletPopup, setTabletPopup] = useState(null);
  const [movilVista, setMovilVista] = useState(null);
  const [movilSeccion, setMovilSeccion] = useState(null);
  const [sheetY, setSheetY] = useState(0);
  const [sheetDragging, setSheetDragging] = useState(false);
  const [sheetExpanded, setSheetExpanded] = useState(false);
  const [modalPerfil, setModalPerfil] = useState(false);
  const [modoTVActivo, setModoTVActivo] = useState(false);
  const [modalPassword, setModalPassword] = useState(false);
  const [movilCatSlideDir, setMovilCatSlideDir] = useState(null); // dirección slide entre categorías
  const [dialogoSalir, setDialogoSalir] = useState(false);
  const [sidebarColapsado, setSidebarColapsado] = useState(() => {
    return storage.getRaw(STORAGE_KEYS.DASHBOARD_SIDEBAR) === 'colapsado';
  });

  // Orden personal del menú (FASE 1.5)
  const [menuOrden, setMenuOrden] = useState({ nivel1: [], nivel2: {} });
  const [modoPersonalizar, setModoPersonalizar] = useState(false);
  const [menuOrdenBackup, setMenuOrdenBackup] = useState(null);
  const [guardandoOrden, setGuardandoOrden] = useState(false);
  const [toastOrden, setToastOrden] = useState(null);

  // Favoritos para bottom nav móvil (FASE 1.6 — máx 4)
  const [menuFavoritos, setMenuFavoritos] = useState([]);
  useEffect(() => {
    storage.set(STORAGE_KEYS.DASHBOARD_SIDEBAR, sidebarColapsado ? 'colapsado' : 'expandido');
  }, [sidebarColapsado]);

  // Detecta overlays full-screen (menú mobile de La Tienda, etc.) que ponen
  // overflow:hidden en <html>. Cuando hay uno, ocultamos el bottom nav para
  // que no tape los últimos items del overlay.
  const [overlayActivo, setOverlayActivo] = useState(false);
  useEffect(() => {
    const sync = () => {
      const v = document.documentElement.style.overflow === 'hidden';
      setOverlayActivo(prev => prev === v ? prev : v);
    };
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['style'] });
    return () => observer.disconnect();
  }, []);
  const sheetDragRef = useRef({ startY: 0, dragging: false, startSheetY: 0 });
  const catSwipeRef = useRef({ startX: 0, startY: 0 });

  const width = useWindowWidth();
  const esCelular = width < 768;
  const esTablet = width >= 768 && width < 1024;
  // En tablet el sidebar siempre va en modo iconos; en desktop depende del usuario
  const modoIconos = esTablet || (!esCelular && sidebarColapsado);

  // TIENDA-ESPACIO: al entrar a La Tienda colapsamos el menu lateral para ganar
  // ancho, y al salir lo restauramos al estado en que estaba antes de entrar.
  // Solo aplica en desktop (en tablet el sidebar ya va siempre en modo iconos y
  // en celular no hay sidebar colapsable). Refs para disparar SOLO en las
  // transiciones entrar/salir y no pisar el toggle manual del usuario dentro de la Tienda.
  const enTienda = activo === 'cotizaciones.tienda';
  const sidebarPrevioTiendaRef = useRef(null);
  const enTiendaPrevRef = useRef(false);
  useEffect(() => {
    if (esCelular || esTablet) return; // colapso automatico solo aplica en desktop
    if (enTienda && !enTiendaPrevRef.current) {
      // entrando a La Tienda: recuerda el estado actual y colapsa
      sidebarPrevioTiendaRef.current = sidebarColapsado;
      if (!sidebarColapsado) setSidebarColapsado(true);
    } else if (!enTienda && enTiendaPrevRef.current) {
      // saliendo de La Tienda: restaura el estado previo
      if (sidebarPrevioTiendaRef.current != null) setSidebarColapsado(sidebarPrevioTiendaRef.current);
    }
    enTiendaPrevRef.current = enTienda;
  }, [enTienda, esCelular, esTablet, sidebarColapsado]);

  // Nombre para mostrar
  const nombreMostrar = nombreUsuario || usuario || 'Usuario';
  const inicialAvatar = nombreMostrar ? nombreMostrar[0].toUpperCase() : 'U';

  // ============================================
  // PERM-3.A+B: Permisos via UsuarioContext (fuente unica)
  // ============================================
  // El context hace el fetch de /api/usuarios/:id/permisos al montar y poblar
  // usuario.permisos. Aqui solo consumimos los helpers (puede, puedeVer,
  // accesoTotal). El componente RutaProtegida envuelve renderContenido()
  // para bloquear modulos sin permiso.
  const { puede, puedeVer, accesoTotal, permisosFetched } = useUsuario();

  const rolNorm = (usuarioObj?.rol || '').toLowerCase();
  const esDireccion = rolNorm.includes('direcc') || rolNorm === 'dirección';

  // PERM-3.C: PROTECCION TRANSITORIA eliminada. El filtro del menu distingue 3 estados:
  //   1. accesoTotal (direccion) → siempre pasa (bypass)
  //   2. !permisosFetched (cargando) → NO pasa (menu vacio mientras se carga,
  //      evita mostrar brevemente modulos que el usuario no debe ver)
  //   3. permisosFetched + chequeo estricto via puede/puedeVer
  // Si llega un usuario con permisos={} (caso edge sin migracion 016): NO ve nada.
  const dejarPasarSeccion = (id) => {
    if (accesoTotal) return true;
    if (!permisosFetched) return false; // deny by default mientras carga
    return puedeVer(id);
  };
  const dejarPasarSub = (clave) => {
    // ENCAPSULAR-TUEXPO (2026-10-15): la entrada a TuExpo no es un permiso de la matriz ni viene con direccion:
    // solo la ve la cuenta marcada como dueño (lo dice el servidor). Para el resto, el modulo no existe.
    if (clave === 'aplicativos.expo') return !!(usuarioObj && usuarioObj.esDueno);
    // AUDITOR (2026-10-21): el tablero es SOLO direccion en el backend (soloDireccion): marcarlo en la matriz no abre nada, asi que
    // el item solo se ensena con acceso total (candado de negocio extra sobre la matriz, igual que en el servidor).
    if (clave === 'administracion.auditor') return !!accesoTotal;
    if (accesoTotal) return true;
    if (!permisosFetched) return false;
    return puede(clave);
  };

  // 1. Aplicar orden personal al menú default ANTES de filtrar por permisos,
  //    así el orden se respeta sobre los ítems que sí quedan visibles.
  const seccionesOrdenadas = aplicarOrden(secciones, menuOrden);
  const seccionesVisibles = seccionesOrdenadas
    // Una seccion con `permiso` (ej. Calendario) se gatea por ESA clave granular,
    // no por puedeVer(id) — asi el tecnico no la ve y direccion/gerencia/operaciones si.
    .filter(s => (s.permiso ? dejarPasarSub(s.permiso) : dejarPasarSeccion(s.id)))
    .map(s => ({
      ...s,
      subs: s.subs.filter(sub => dejarPasarSub(`${s.id}.${sub.id}`)),
    }))
    .filter(s => s.esFlat || s.subs.length > 0);

  // Cargar orden personal y favoritos al montar (y al cambiar usuario).
  // FIX-SESION-OBSERVABILIDAD: usa fetchConSesion para que un 401 dispare la
  // redireccion automatica al login. Si la red falla, loggea (silencioso al
  // usuario porque es carga inicial, pero queda rastro en consola).
  useEffect(() => {
    const token = localStorage.getItem('beep_token');
    if (!token) return;
    fetchConSesion(`${API_BASE}/api/menu-orden`, {
      headers: { 'Authorization': `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => { if (d.ok) setMenuOrden({ nivel1: d.nivel1 || [], nivel2: d.nivel2 || {} }); })
      .catch(e => {
        if (e && e.code !== 'SESION_EXPIRADA') console.error('[menu-orden GET]', e);
      });
    fetchConSesion(`${API_BASE}/api/menu-favoritos`, {
      headers: { 'Authorization': `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => {
        if (d.ok) setMenuFavoritos((d.favoritos || []).map(f => f.item_id));
      })
      .catch(e => {
        if (e && e.code !== 'SESION_EXPIRADA') console.error('[menu-favoritos GET]', e);
      });
  }, [usuarioObj?.id]);

  const toggleFavorito = async (secId) => {
    const token = localStorage.getItem('beep_token');
    if (!token) { manejarSesionExpirada(); return; }
    const esFav = menuFavoritos.includes(secId);
    // Pre-chequeo UX: si intenta agregar y ya hay 4, avisa antes de llamar
    if (!esFav && menuFavoritos.length >= 4) {
      setToastOrden('⚠️ Ya tienes 4 favoritos. Desmarca uno primero.');
      setTimeout(() => setToastOrden(null), 3000);
      return;
    }
    try {
      const resp = await fetchConSesion(`${API_BASE}/api/menu-favoritos/toggle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ item_id: secId }),
      });
      const r = await resp.json();
      if (resp.status === 409) {
        setToastOrden(`⚠️ ${r.error}`);
        setTimeout(() => setToastOrden(null), 3000);
        return;
      }
      if (r.ok) {
        setMenuFavoritos((r.favoritos || []).map(f => f.item_id));
      } else {
        setToastOrden('No se pudo guardar el favorito. Intenta de nuevo.');
        setTimeout(() => setToastOrden(null), 4000);
      }
    } catch (e) {
      if (e && e.code === 'SESION_EXPIRADA') return; // redirect ya en curso
      console.error('[toggleFavorito]', e);
      setToastOrden('No se pudo guardar el favorito. Revisa tu conexion.');
      setTimeout(() => setToastOrden(null), 4000);
    }
  };

  const sensoresDnD = useSensoresDnD();

  const entrarModoPersonalizar = () => {
    setMenuOrdenBackup(menuOrden);
    setModoPersonalizar(true);
  };

  const salirModoPersonalizar = () => {
    setModoPersonalizar(false);
    setMenuOrdenBackup(null);
  };

  const cancelarPersonalizar = () => {
    if (menuOrdenBackup) setMenuOrden(menuOrdenBackup);
    salirModoPersonalizar();
  };

  const reordenarNivel1 = (activeId, overId) => {
    const ids = seccionesOrdenadas.map(s => s.id);
    const oldIdx = ids.indexOf(activeId);
    const newIdx = ids.indexOf(overId);
    if (oldIdx < 0 || newIdx < 0 || oldIdx === newIdx) return;
    const nuevoOrden = arrayMove(ids, oldIdx, newIdx);
    setMenuOrden(prev => ({
      ...prev,
      nivel1: nuevoOrden.map((id, orden) => ({ item_id: id, orden })),
    }));
  };

  const reordenarNivel2 = (parentId, activeId, overId) => {
    const seccion = seccionesOrdenadas.find(s => s.id === parentId);
    if (!seccion) return;
    const ids = seccion.subs.map(s => s.id);
    const oldIdx = ids.indexOf(activeId);
    const newIdx = ids.indexOf(overId);
    if (oldIdx < 0 || newIdx < 0 || oldIdx === newIdx) return;
    const nuevoOrden = arrayMove(ids, oldIdx, newIdx);
    setMenuOrden(prev => ({
      ...prev,
      nivel2: {
        ...prev.nivel2,
        [parentId]: nuevoOrden.map((id, orden) => ({ item_id: id, orden })),
      },
    }));
  };

  const guardarOrden = async () => {
    const token = localStorage.getItem('beep_token');
    if (!token) { manejarSesionExpirada(); return; }
    setGuardandoOrden(true);
    try {
      const nivel1Ids = (menuOrden.nivel1 || []).map(x => x.item_id);
      const nivel2Simple = {};
      for (const k of Object.keys(menuOrden.nivel2 || {})) {
        nivel2Simple[k] = (menuOrden.nivel2[k] || []).map(x => x.item_id);
      }
      const resp = await fetchConSesion(`${API_BASE}/api/menu-orden`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ nivel1: nivel1Ids, nivel2: nivel2Simple }),
      });
      const r = await resp.json();
      if (r.ok) {
        setToastOrden('✅ Orden guardado');
        setTimeout(() => setToastOrden(null), 3000);
        salirModoPersonalizar();
      } else {
        setToastOrden('⚠️ No se pudo guardar');
        setTimeout(() => setToastOrden(null), 3000);
      }
    } catch (e) {
      if (e && e.code === 'SESION_EXPIRADA') return;
      console.error('[guardarOrden]', e);
      setToastOrden('⚠️ No se pudo guardar. Revisa tu conexion.');
      setTimeout(() => setToastOrden(null), 4000);
    } finally {
      setGuardandoOrden(false);
    }
  };

  const restablecerOrden = async () => {
    if (!window.confirm('¿Restablecer al orden original?')) return;
    const token = localStorage.getItem('beep_token');
    if (!token) { manejarSesionExpirada(); return; }
    setGuardandoOrden(true);
    try {
      await fetchConSesion(`${API_BASE}/api/menu-orden`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ nivel1: [], nivel2: {} }),
      });
      setMenuOrden({ nivel1: [], nivel2: {} });
      setToastOrden('🔄 Restablecido al orden original');
      setTimeout(() => setToastOrden(null), 3000);
      salirModoPersonalizar();
    } catch (e) {
      if (e && e.code === 'SESION_EXPIRADA') return;
      console.error('[restablecerOrden]', e);
      setToastOrden('⚠️ No se pudo restablecer. Revisa tu conexion.');
      setTimeout(() => setToastOrden(null), 4000);
    } finally {
      setGuardandoOrden(false);
    }
  };

  // Solicitar permiso de notificaciones al cargar (una vez)
  useEffect(() => {
    const timer = setTimeout(() => { solicitarPermiso(); }, 5000);
    return () => clearTimeout(timer);
  }, []);

  const seccionActiva = activo.split('.')[0];
  const subActivo = activo.split('.')[1];
  // PERM-FIX-TABS: usar seccionesVisibles para que tabs/subs internos hereden el filtro de permisos
  const seccionInfo = seccionesVisibles.find(s => s.id === seccionActiva);
  const subInfo = seccionInfo ? seccionInfo.subs.find(s => s.id === subActivo) : null;

  const cambiarPagina = (ruta) => {
    if (ruta === activo) return;
    const sec = ruta.split('.')[0];
    if (esCelular) {
      setAnimando(true);
      setTimeout(() => {
        setActivo(ruta);
        setSeccionAbierta(sec);
        setMovilVista(null);
        setMovilSeccion(null);
        setMenuAbierto(false);
        setTimeout(() => setAnimando(false), 50);
      }, 300);
    } else {
      setActivo(ruta);
      setSeccionAbierta(sec);
      setMenuAbierto(false);
      setTabletPopup(null);
    }
  };

  // Helper: cerrar todo el menú móvil
  const cerrarMenuMovil = () => {
    setMovilVista(null);
    setMovilSeccion(null);

    setMenuAbierto(false);
    setSheetExpanded(false);
    setSheetY(0);
  };

  // Celular: abrir submenús de una sección
  const abrirSubMenus = (secId) => {
    // Si la sección no tiene submenús, navega directo (no abrir sheet vacío).
    // PERM-FIX-TABS: seccionesVisibles → handler defensivo, si la seccion no esta visible no se abre
    const sec = seccionesVisibles.find(s => s.id === secId);
    if (sec && (!sec.subs || sec.subs.length === 0)) {
      cambiarPagina(secId);
      cerrarMenuMovil();
      return;
    }
    setMovilSeccion(secId);
    setMovilVista('submenus');

    setSheetY(0);
    setSheetExpanded(false);
    setMovilCatSlideDir(null);
  };

  // Celular: tocar tarjeta de submenú → abrir página directo y cerrar menú
  const entrarSubMenu = (secId, subIdx) => {
    // PERM-FIX-TABS: seccionesVisibles → handler defensivo
    const sec = seccionesVisibles.find(s => s.id === secId);
    if (!sec) return;
    const ruta = sec.id + '.' + sec.subs[subIdx].id;
    cambiarPagina(ruta);
  };

  // Swipe izquierda/derecha para cambiar de categoría (en grid de submenús)
  const onCatSwipeStart = (e) => {
    catSwipeRef.current.startX = e.touches[0].clientX;
    catSwipeRef.current.startY = e.touches[0].clientY;
  };
  const onCatSwipeEnd = (e) => {
    const dx = e.changedTouches[0].clientX - catSwipeRef.current.startX;
    const dy = Math.abs(e.changedTouches[0].clientY - catSwipeRef.current.startY);
    if (Math.abs(dx) < 60 || dy > 100) return;
    const secsList = seccionesVisibles;
    const idxActual = secsList.findIndex(s => s.id === movilSeccion);
    if (idxActual === -1) return;
    if (dx < -60 && idxActual < secsList.length - 1) {
      // Swipe izquierda → siguiente categoría
      setMovilCatSlideDir('left');
      setMovilSeccion(secsList[idxActual + 1].id);
  
    } else if (dx > 60 && idxActual > 0) {
      // Swipe derecha → categoría anterior
      setMovilCatSlideDir('right');
      setMovilSeccion(secsList[idxActual - 1].id);
  
    }
  };

  const toggleSeccion = (secId) => {
    // Si la sección no tiene submenús (ej. Expedientes en FASE 2A), navega directo a su vista raíz.
    // PERM-FIX-TABS: seccionesVisibles → handler defensivo
    const sec = seccionesVisibles.find(s => s.id === secId);
    if (sec && (!sec.subs || sec.subs.length === 0)) {
      cambiarPagina(secId);
      setSeccionAbierta(secId);
      return;
    }
    if (seccionAbierta === secId) {
      setSeccionAbierta(null);
    } else {
      setSeccionAbierta(secId);
    }
  };

  // ============================================
  // ARRASTRE VERTICAL DEL SHEET (mejorado)
  // ============================================
  const onSheetTouchStart = (e) => {
    sheetDragRef.current.startY = e.touches[0].clientY;
    sheetDragRef.current.dragging = true;
    sheetDragRef.current.startSheetY = sheetY;
    setSheetDragging(true);
  };
  const onSheetTouchMove = (e) => {
    if (!sheetDragRef.current.dragging) return;
    const dy = e.touches[0].clientY - sheetDragRef.current.startY;
    // Permitir arrastrar hacia abajo (positivo) y hacia arriba (negativo hasta cierto punto)
    if (sheetExpanded) {
      // Si está expandido, solo permitir arrastrar hacia abajo
      if (dy > 0) setSheetY(dy);
    } else {
      // Si no está expandido, mover libremente
      setSheetY(dy);
    }
  };
  const onSheetTouchEnd = () => {
    sheetDragRef.current.dragging = false;
    setSheetDragging(false);
    const dy = sheetY;
    if (dy > 100) {
      // Arrastró hacia abajo > 100px → cerrar
      cerrarMenuMovil();
    } else if (dy < -80 && !sheetExpanded) {
      // Arrastró hacia arriba > 80px → expandir a pantalla completa
      setSheetExpanded(true);
      setSheetY(0);
    } else {
      // Regresar a posición original
      setSheetY(0);
    }
  };

  // ============================================
  // BOTÓN FÍSICO DE ATRÁS (Android)
  // ============================================
  useEffect(() => {
    if (!esCelular) return;
    // Pushear estado al history cuando se abre un menú
    const pushState = () => {
      window.history.pushState({ beepMenu: true }, '');
    };

    const handlePopState = (e) => {
      e.preventDefault();

      // Nivel 1: Si hay menú abierto, cerrarlo
      if (movilVista === 'submenus' || movilVista === 'secciones') {
        cerrarMenuMovil();
        pushState();
        return;
      }

      // Nivel 2: Si estamos en chat de agentes con canal abierto, cerrar canal
      if (activo === 'mitrabajo.agentes-chat') {
        window.dispatchEvent(new CustomEvent('beep:canal-cerrar'));
        pushState();
        return;
      }

      // Nivel 3: Si estamos en un sub que no es el primero, ir al primer sub
      const secActual = secciones.find(s => s.id === seccionActiva);
      if (secActual && subActivo !== secActual.subs[0].id) {
        setActivo(`${seccionActiva}.${secActual.subs[0].id}`);
        pushState();
        return;
      }

      // Nivel 4: Si no estamos en mitrabajo, ir a mitrabajo
      if (seccionActiva !== 'mitrabajo') {
        setActivo('mitrabajo.midia');
        pushState();
        return;
      }

      // Nivel 5: Estamos en el inicio, preguntar si quiere salir
      setDialogoSalir(true);
      pushState();
    };

    // Siempre mantener un estado en el history para interceptar
    pushState();
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [esCelular, movilVista, activo, seccionActiva, subActivo]);

  const renderContenido = () => {
    const clave = activo;
    const sec = secciones.find(s => s.id === seccionActiva);
    const sub = sec ? sec.subs.find(s => s.id === subActivo) : null;

    // Inicio
    // ===== MI TRABAJO (nuevo menú) =====
    if (clave === 'mitrabajo.midia') return <MiDia nombre={nombreMostrar} rol={rolUsuario || 'Administrador'} esCelular={esCelular} onNavegar={setActivo} />;
    if (clave === 'mitrabajo.agenda') return <MiAgenda
      onCrearRequerimiento={() => setActivo('operaciones.expedientes-todos')}
      onCrearTarea={() => setActivo('mitrabajo.tareas')}
      onCrearProyecto={() => setActivo('operaciones.expedientes-todos')}
      tabInicial={tabAgenda}
    />;
    if (clave === 'mitrabajo.tareas') return <Tareas rolUsuario={rolUsuario} nombreMostrar={nombreMostrar} esCelular={esCelular} onNavegar={setActivo} />;
    if (clave === 'mitrabajo.gastos') return <Gastos rol={rolUsuario} nombre={nombreMostrar} esCelular={esCelular} />;
    if (clave === 'mitrabajo.agentes') {
      console.log('ROL ENVIADO A CENTRO AGENTES:', rolUsuario);
      return <CentroAgentes rol={rolUsuario} nombre={nombreMostrar} onNavegar={setActivo} />;
    }
    if (clave === 'mitrabajo.agentes-chat') {
      return <CanalAgentes nombre={nombreMostrar} rol={rolUsuario} canalInicialId={canalActivoId} onNavegar={setActivo} />;
    }
    // ===== EXPEDIENTES (FASE 2A — CRM pre-venta + cotizaciones) =====
    // Sin submenús: el módulo nuevo es el handler único para 'operaciones'.
    // Los aliases legacy (operaciones.expedientes-todos / .programar) siguen
    // resolviendo aquí porque las cards del dashboard navegan a esas rutas.
    // CALENDARIO-E1: ficha directa del expediente asignado (tecnico restringido).
    // Sin la lista/kanban/actividad globales; el backend valida el acceso puntual
    // y oculta el dinero. Volver = Mi Trabajo.
    if (clave === 'mitrabajo.mi-expediente') {
      if (expedienteAbrirId == null) {
        const irMiTrabajo = () => { setActivoRaw('mitrabajo.tareas'); storage.set(STORAGE_KEYS.DASHBOARD_ACTIVE_TAB, 'mitrabajo.tareas'); };
        return (
          <div style={{ padding: 24, color: '#5e666e', fontSize: 14 }}>
            Abre un expediente desde <button onClick={irMiTrabajo} style={{ border: 'none', background: 'none', color: '#185FA5', fontWeight: 700, cursor: 'pointer', padding: 0 }}>Mi Trabajo</button>.
          </div>
        );
      }
      return (
        <FichaExpediente
          id={expedienteAbrirId}
          esCelular={esCelular}
          onVolver={() => { setExpedienteAbrirId(null); setActivoRaw('mitrabajo.tareas'); storage.set(STORAGE_KEYS.DASHBOARD_ACTIVE_TAB, 'mitrabajo.tareas'); }}
          onCambiarId={(nid) => setExpedienteAbrirId(nid)}
        />
      );
    }
    if (clave === 'operaciones') return <Expedientes esCelular={esCelular} />;
    if (clave === 'operaciones.expedientes-todos') return (
      <Expedientes
        esCelular={esCelular}
        modoSeleccion={!!selExpCot}
        seleccionFolio={selExpCot?.folio || ''}
        onElegir={onElegirExpediente}
        onCancelarSeleccion={onCancelarExpediente}
        aperturaInicialId={expedienteAbrirId}
        onAperturaConsumida={() => setExpedienteAbrirId(null)}
      />
    );
    if (clave === 'operaciones.programar') return <Expedientes esCelular={esCelular} />;
    if (clave === 'operaciones.calendario') return <CalendarioCampo esCelular={esCelular} />;
    if (clave === 'calendario') return <CalendarioCampo esCelular={esCelular} />;
    if (clave === 'operaciones.servicios') return <ServiciosDelDia esCelular={esCelular} />;
    if (clave === 'operaciones.mantenimientos') return <MantenimientosExp esCelular={esCelular} />;
    if (clave === 'operaciones.nueva-instalacion') return <Proyectos pestana="instalaciones" rolUsuario={rolUsuario} esCelular={esCelular} />;
    if (clave === 'operaciones.levantamientos') return <Levantamiento />;
    // ===== COTIZACIONES =====
    if (clave === 'cotizaciones.en-cotizacion') return <CotizacionesLista />;
    if (clave === 'cotizaciones.previsualizador') return <Previsualizador carritoId={carritoPreviewId} cotizacionId={cotizacionEditId} volver={() => setActivo(cotizacionEditId != null ? 'cotizaciones.en-cotizacion' : 'cotizaciones.tienda')} />;
    // ===== APLICATIVOS =====
    if (clave === 'aplicativos.expo') return null; // Handled by useEffect → navigate('/expo')
    if (clave === 'aplicativos.beeptv') return <BeepTV esCelular={esCelular} usuario={usuarioObj} />;
    // ===== ALMACÉN =====
    // SUB-ALMACENES: Central · Oficina · Tecnicos · Instalados. Las claves viejas (materialservicios, materialinstalaciones,
    // herramientas) ya no estan en el menu; si alguien las tiene guardadas como pestana activa, abren Central.
    if (clave === 'almacen.inventario' || clave === 'almacen.materialservicios' || clave === 'almacen.materialinstalaciones' || clave === 'almacen.herramientas') return <Almacen pestana="central" esCelular={esCelular} />;
    if (clave === 'almacen.oficina') return <Almacen pestana="oficina" esCelular={esCelular} />;
    if (clave === 'almacen.tecnicos') return <Almacen pestana="tecnicos" esCelular={esCelular} />;
    if (clave === 'almacen.equiposinstalados') return <Almacen pestana="instalados" esCelular={esCelular} />;
    // ALMACEN v3: Compras es seccion propia; 'almacen.compras' (ruta vieja de la Tienda) abre lo mismo.
    if (clave === 'compras' || clave === 'almacen.compras') return <Compras esCelular={esCelular} />;
    // ===== CLIENTES (FASE 1 — nuevo modelo grupo/razón/sucursal/persona) =====
    if (clave === 'comercial.clientes') return <ClientesNuevo esCelular={esCelular} />;
    if (clave === 'comercial.directorio') return <Directorio esCelular={esCelular} />;
    // ===== ADMINISTRACIÓN =====
    if (clave === 'administracion.rrhh') return <RecursosHumanos rol={rolUsuario} esCelular={esCelular} />;
    if (clave === 'administracion.finanzas') return <Gastos rol={rolUsuario} nombre={nombreMostrar} todos={true} esCelular={esCelular} />;
    if (clave === 'administracion.usuarios') return <Usuarios rolUsuario={rolUsuario} esCelular={esCelular} />;
    if (clave === 'administracion.configuracion') return <Configuracion />;
    if (clave === 'administracion.auditor') return <TableroAuditor esCelular={esCelular} />;
    if (clave === 'administracion.registro-accesos') return <RegistroAccesos esCelular={esCelular} />;
    if (clave === 'administracion.busquedas-sin-resultado') return <BusquedasSinResultado />;
    if (clave === 'administracion.ajustes-vigilante') return <AjustesVigilante />;
    if (clave === 'administracion.capturar-importes') return <CapturaImportes />;
    if (clave === 'administracion.empresa') return <EmpresaDatos />;
    // FLUJOS-FIJOS: pantallas de workflows configurables dormidas (no borradas).
    // if (clave === 'administracion.workflows') return <Workflows esCelular={esCelular} />;
    // if (clave === 'administracion.workflows-personas') return <WorkflowsPersonas esCelular={esCelular} />;
    // if (clave === 'administracion.estados') return <CatalogoEstados />;
    if (clave === 'administracion.test-subir-imagen') return <TestSubirImagen />;
    if (clave === 'cotizaciones.tienda') return <Tienda />;
    if (clave === 'cotizaciones.catalogo') return <CatalogoAdmin />;
    if (clave === 'cotizaciones.listas') return <ListasPactadas />;
    if (clave === 'cotizaciones.colecciones') return <Colecciones />;
    if (clave === 'cotizaciones.mis-links') return <MisLinks />;
    if (clave === 'cotizaciones.tipo-cambio') return <TipoCambioConfig />;
    if (clave === 'cotizaciones.configuracion') return <CotizadorConfig />;
    if (clave === 'cotizaciones.syscom') return <SyscomAdmin />;
    // ===== MARKETING (rutas canónicas más abajo) =====
    // ===== DASHBOARD EJECUTIVO =====
    if (clave === 'dashboard.general') return <DashboardDirector rol={rolUsuario} seccion="general" esCelular={esCelular} />;
    if (clave === 'dashboard.operaciones') return <DashboardDirector rol={rolUsuario} seccion="operaciones" esCelular={esCelular} />;
    if (clave === 'dashboard.comercial') return <DashboardDirector rol={rolUsuario} seccion="comercial" esCelular={esCelular} />;
    if (clave === 'dashboard.finanzas') return <DashboardDirector rol={rolUsuario} seccion="finanzas" esCelular={esCelular} />;

    if (clave === 'inicio.midia') return <MiDia nombre={nombreMostrar} rol={rolUsuario || 'Administrador'} esCelular={esCelular} onNavegar={setActivo} />;
    if (clave === 'inicio.tareas') return <Tareas rolUsuario={rolUsuario} nombreMostrar={nombreMostrar} esCelular={esCelular} onNavegar={setActivo} />;
    if (clave === 'inicio.ejecutivo') return <DashboardPanel esCelular={esCelular} cambiarPagina={cambiarPagina} />;
    if (clave === 'inicio.vistaenvivo') return <CentroControl onModoTV={setModoTVActivo} />;
    // Requerimientos
    if (clave === 'requerimientos.agenda') return <MiAgenda
      onCrearRequerimiento={() => setActivo('requerimientos.nuevo')}
      onCrearTarea={() => setActivo('inicio.midia')}
      onCrearProyecto={() => setActivo('operaciones.expedientes-todos')}
      tabInicial={tabAgenda}
    />;
    if (clave === 'requerimientos.nuevo') return <Requerimientos pestanaInicial="nuevo" abrirFormulario={true} usuario={nombreMostrar} esCelular={esCelular} />;
    if (clave === 'requerimientos.enproceso') return <Requerimientos pestanaInicial="enproceso" usuario={nombreMostrar} esCelular={esCelular} />;
    if (clave === 'requerimientos.historial') return <Requerimientos pestanaInicial="historial" usuario={nombreMostrar} esCelular={esCelular} />;
    // Comercial
    if (clave === 'comercial.prospectos') return <Leads />;
    if (clave === 'comercial.cotizaciones') return <PipelineCotizaciones rol={rolUsuario} nombre={nombreMostrar} />;
    if (clave === 'comercial.levantamientos') return <Levantamiento />;
    // Clientes
    if (clave === 'clientes.empresas') return <Clientes pestana="empresas" esCelular={esCelular} />;
    if (clave === 'clientes.contactos') return <Clientes pestana="contactos" esCelular={esCelular} />;
    if (clave === 'clientes.contratos') return <Clientes pestana="contratos" esCelular={esCelular} />;
    // Servicios
    if (clave === 'servicios.correctivos') return <Servicios filtroTipo="correctivo" rolUsuario={rolUsuario} esCelular={esCelular} />;
    if (clave === 'servicios.mantenimientos') return <Servicios filtroTipo="mantenimiento" rolUsuario={rolUsuario} esCelular={esCelular} />;
    if (clave === 'servicios.todos') return <Servicios rolUsuario={rolUsuario} esCelular={esCelular} />;
    if (clave === 'servicios.monitoreo') return <CentroControl onModoTV={setModoTVActivo} />;
    // Compatibilidad legacy (rutas viejas de Proyectos por si queda navegación antigua por ahí)
    if (clave === 'proyectos.activos') return <Expedientes esCelular={esCelular} />;
    if (clave === 'proyectos.calendario') return <Calendario />;
    if (clave === 'proyectos.grupos') return <GruposProyectos />;
    // Inventario
    if (clave === 'inventario.almacen') return <Inventario />;
    // RRHH
    if (clave === 'rrhh.asistencias') return <RecursosHumanos pestanaInicial="asistencia" />;
    // Marketing
    if (clave === 'marketing.campanas') return <Marketing pestanaInicial="campanas" />;
    if (clave === 'marketing.calendario') return <Marketing pestanaInicial="calendario" />;
    if (clave === 'marketing.biblioteca') return <Marketing pestanaInicial="biblioteca" />;
    if (clave === 'marketing.recursos') return <Marketing pestanaInicial="recursos" />;
    if (clave === 'marketing.credenciales') return <Marketing pestanaInicial="credenciales" />;
    if (clave === 'marketing.metricas') return <Marketing pestanaInicial="metricas" />;
    if (clave === 'marketing.agentes') return <Marketing pestanaInicial="agentes" />;
    // Configuración
    if (clave === 'configuracion.usuarios') return <Usuarios rolUsuario={rolUsuario} esCelular={esCelular} />;
    if (clave === 'configuracion.catalogo') return <Configuracion pestanaInicial="servicios" />;
    if (clave === 'configuracion.empresa') return <Configuracion pestanaInicial="empresa" />;
    // Otros
    if (clave === 'otros.leads') return <Leads />;
    if (clave === 'otros.gruposproyectos') return <GruposProyectos />;
    if (clave === 'otros.configuracion') return <Configuracion />;

    // Placeholder para módulos no construidos
    if (sec && sub) return <PlaceholderModulo seccion={seccionActiva} sub={sub} />;
    return null;
  };

  const PP = "'Poppins', sans-serif";

  // Aplicativos — navegar a /expo con React Router
  useEffect(() => {
    if (activo === 'aplicativos.expo' && onIrAExpo) {
      onIrAExpo();
      setActivoRaw('mitrabajo.midia');
    }
  }, [activo, onIrAExpo]);

  // Marketing · Campañas — navega internamente a /campanas
  const navigate = useNavigate();
  useEffect(() => {
    if (activo === 'marketing.campanas') {
      navigate('/campanas');
      setActivoRaw('mitrabajo.midia');
    }
  }, [activo, navigate]);

  return (
    // VER-COMO: wrapper en columna para que el banner ambar (si hay modo activo)
    // EMPUJE el shell hacia abajo en vez de taparlo. Sin modo activo el banner
    // renderiza null y este wrapper es transparente al layout (flex 1 = 100dvh).
    <div style={{ display: 'flex', flexDirection: 'column', height: '100dvh' }}>
      <BannerVerComo />
    <div style={{ display: 'flex', flex: 1, minHeight: 0, fontFamily: PP, background: colores.fondo, flexDirection: esCelular ? 'column' : 'row', overscrollBehavior: 'contain' }}>
      {toastOrden && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          background: '#1a3a5c', color: '#fff',
          padding: '12px 18px', borderRadius: 10, fontSize: 13,
          fontFamily: PP, boxShadow: '0 4px 14px rgba(0,0,0,0.25)',
          zIndex: 2000,
        }}>
          {toastOrden}
        </div>
      )}

      <style>{`
        html, body { overscroll-behavior-y: contain; }
        @keyframes salida {
          0%   { opacity: 1; transform: scale(1) translate(0, 0); }
          100% { opacity: 0; transform: scale(0.7) translate(30%, -30%); }
        }
        @keyframes entrada {
          0%   { opacity: 0; }
          100% { opacity: 1; }
        }
        @keyframes slideDown {
          0%   { max-height: 0; opacity: 0; }
          100% { max-height: 400px; opacity: 1; }
        }
        @keyframes fadeIn {
          0%   { opacity: 0; transform: translateY(-8px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes sheetUp {
          0%   { transform: translateY(100%); }
          100% { transform: translateY(0); }
        }
        @keyframes slideFromRight {
          0%   { transform: translateX(100%); opacity: 0; }
          100% { transform: translateX(0); opacity: 1; }
        }
        @keyframes cardPop {
          0%   { opacity: 0; transform: scale(0.85) translateY(20px); }
          100% { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes slideInRight {
          0%   { transform: translateX(100%); opacity: 0; }
          100% { transform: translateX(0); opacity: 1; }
        }
        @keyframes slideInLeft {
          0%   { transform: translateX(-100%); opacity: 0; }
          100% { transform: translateX(0); opacity: 1; }
        }
        @keyframes slideOutLeft {
          0%   { transform: translateX(0); opacity: 1; }
          100% { transform: translateX(-100%); opacity: 0; }
        }
        @keyframes slideOutRight {
          0%   { transform: translateX(0); opacity: 1; }
          100% { transform: translateX(100%); opacity: 0; }
        }
        .pagina-salida   { animation: salida  0.3s  ease-in  forwards; }
        .pagina-entrada  { animation: entrada 0.25s ease-out forwards; }
        .submenu-slide   { animation: slideDown 0.25s ease-out forwards; overflow: hidden; }
        .popup-fade      { animation: fadeIn 0.2s ease-out forwards; }
        .sheet-up        { animation: sheetUp 0.3s cubic-bezier(0.32,0.72,0,1) forwards; }
        .slide-right     { animation: slideFromRight 0.3s cubic-bezier(0.32,0.72,0,1) forwards; }
        .card-pop        { animation: cardPop 0.3s cubic-bezier(0.32,0.72,0,1) forwards; }
        .slide-in-right  { animation: slideInRight 0.3s cubic-bezier(0.32,0.72,0,1) forwards; }
        .slide-in-left   { animation: slideInLeft 0.3s cubic-bezier(0.32,0.72,0,1) forwards; }
        .sidebar-item:hover { background: rgba(255,255,255,0.12) !important; }
        .sidebar-sub:hover  { background: rgba(255,255,255,0.12) !important; }
        .sidebar-nav { scrollbar-width: thin; scrollbar-color: transparent transparent; }
        .sidebar-nav::-webkit-scrollbar { width: 5px; }
        .sidebar-nav::-webkit-scrollbar-track { background: transparent; }
        .sidebar-nav::-webkit-scrollbar-thumb { background: transparent; border-radius: 4px; }
        .sidebar-nav:hover { scrollbar-color: rgba(255,255,255,0.25) transparent; }
        .sidebar-nav:hover::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.25); }
        .sidebar-nav:hover::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.4); }
      `}</style>

      {/* ============================================
          SIDEBAR — Desktop y Tablet
          ============================================ */}
      {!esCelular && !modoTVActivo && (
        <div style={{
          width: esTablet ? '60px' : (sidebarColapsado ? '64px' : '220px'),
          background: '#4e5357',
          display: 'flex',
          flexDirection: 'column',
          flexShrink: 0,
          transition: 'width 0.25s ease',
          position: 'relative',
        }}>
          {/* Botón colapsar — solo desktop */}
          {!esTablet && (
            <button
              onClick={() => setSidebarColapsado(!sidebarColapsado)}
              style={{
                position: 'absolute', top: 12, right: -12,
                width: 24, height: 24,
                background: '#4e5357', border: '2px solid white',
                borderRadius: '50%', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'white', fontSize: 12, zIndex: 10,
                padding: 0, lineHeight: 1,
              }}
              title={sidebarColapsado ? 'Expandir' : 'Colapsar'}
            >
              {sidebarColapsado ? '›' : '‹'}
            </button>
          )}

          {/* Logo */}
          <div style={{ padding: '20px 16px', borderBottom: '1px solid rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: modoIconos ? 'center' : 'flex-start' }}>
            {modoIconos ? (
              <img src={encodeURI('/icono beep blanco fondo trasparente.png')} alt="Beep" style={{ width: '40px', height: '40px' }} />
            ) : (
              <img src={encodeURI('/Logo Horizontal blanco.png')} alt="Beep" style={{ maxWidth: '160px', width: '100%', height: 'auto' }} />
            )}
          </div>

          {/* Secciones del menú */}
          <nav className="sidebar-nav" style={{ padding: '8px 0 8px', flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
            {modoPersonalizar && !modoIconos && (
              <BarraPersonalizar
                esCelular={esCelular}
                guardando={guardandoOrden}
                onGuardar={guardarOrden}
                onCancelar={cancelarPersonalizar}
                onRestablecer={restablecerOrden}
              />
            )}
            {(() => {
              const renderCabecera = (sec, idx) => {
                const esActiva = seccionActiva === sec.id;
                const estaAbierta = seccionAbierta === sec.id;
                return (
                  <div
                    className="sidebar-item"
                    title={modoIconos ? sec.label : undefined}
                    onClick={() => {
                      // En modo personalizar, el click en la cabecera abre/cierra
                      // la sección para reordenar sus subs (el drag solo va en el handle ≡).
                      if (modoPersonalizar) {
                        if (!modoIconos) toggleSeccion(sec.id);
                        return;
                      }
                      if (modoIconos) {
                        // CO-BUG-MENU-MIN: una seccion FLAT (sin subs, ej. Expedientes)
                        // debe NAVEGAR directo igual que expandido. Antes abria un popup
                        // vacio (sec.subs.map sin items) y el icono quedaba muerto.
                        if (!sec.subs || sec.subs.length === 0) {
                          setTabletPopup(null);
                          cambiarPagina(sec.id);
                        } else {
                          setTabletPopup(tabletPopup === sec.id ? null : sec.id);
                        }
                      } else {
                        toggleSeccion(sec.id);
                      }
                    }}
                    style={{
                      padding: modoIconos ? '14px' : '10px 16px',
                      // En personalizar la cabecera expande/colapsa (pointer), solo los subs van con cursor 'default'
                      cursor: (modoPersonalizar && modoIconos) ? 'default' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: modoIconos ? 0 : '10px',
                      justifyContent: modoIconos ? 'center' : 'space-between',
                      color: esActiva ? '#ffffff' : 'rgba(255,255,255,0.85)',
                      fontWeight: esActiva ? 700 : 400,
                      background: esActiva ? 'rgba(255,255,255,0.12)' : 'transparent',
                      borderLeft: esActiva ? '3px solid #5c9adb' : '3px solid transparent',
                      transition: 'all 0.15s',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: modoIconos ? 0 : '10px' }}>
                      {!modoIconos && !modoPersonalizar && (() => {
                        const esFav = menuFavoritos.includes(sec.id);
                        return (
                          <span
                            onClick={(e) => { e.stopPropagation(); toggleFavorito(sec.id); }}
                            title={esFav ? 'Quitar de favoritos' : 'Marcar como favorito para móvil'}
                            style={{
                              cursor: 'pointer', fontSize: 14, lineHeight: 1,
                              color: esFav ? '#f59e0b' : 'rgba(255,255,255,0.35)',
                              flexShrink: 0, userSelect: 'none', width: 16, textAlign: 'center',
                            }}
                          >{esFav ? '★' : '☆'}</span>
                        );
                      })()}
                      <IconoSeccion item={sec} size={modoIconos ? 22 : 20} color={esActiva ? '#ffffff' : 'rgba(255,255,255,0.85)'} />
                      {!modoIconos && (
                        <span style={{ fontSize: '13px', whiteSpace: 'nowrap' }}>{sec.label}</span>
                      )}
                    </div>
                    {!modoIconos && (
                      <span style={{ fontSize: '10px', transition: 'transform 0.2s', transform: estaAbierta ? 'rotate(180deg)' : 'rotate(0deg)', opacity: 0.6 }}>▾</span>
                    )}
                  </div>
                );
              };

              const renderSub = (sec, sub) => {
                const ruta = sec.id + '.' + sub.id;
                const subEsActivo = activo === ruta;
                const tieneModulo = rutasConModulo[ruta];
                return (
                  <div
                    className="sidebar-sub"
                    onClick={() => { if (!modoPersonalizar) cambiarPagina(ruta); }}
                    style={{
                      padding: '8px 16px 8px 46px',
                      cursor: modoPersonalizar ? 'default' : 'pointer',
                      fontSize: '12px',
                      fontWeight: subEsActivo ? '600' : '400',
                      color: subEsActivo ? '#ffffff' : 'rgba(255,255,255,0.85)',
                      background: subEsActivo ? 'rgba(255,255,255,0.12)' : 'transparent',
                      transition: 'all 0.15s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <span style={{ width: '4px', height: '4px', borderRadius: '50%', background: subEsActivo ? '#5c9adb' : 'rgba(255,255,255,0.45)', flexShrink: 0 }} />
                    {sub.label}
                    {!tieneModulo && (
                      <span style={{ fontSize: '8px', padding: '1px 6px', borderRadius: '6px', background: 'rgba(255,255,255,0.25)', color: '#fff', fontWeight: '600', marginLeft: 'auto' }}>Pronto</span>
                    )}
                  </div>
                );
              };

              const renderSeccion = (sec, idx) => {
                const estaAbierta = seccionAbierta === sec.id;
                const cuerpo = (
                  <div style={{ borderTop: idx === 0 ? 'none' : '1px solid rgba(255,255,255,0.08)' }}>
                    {renderCabecera(sec, idx)}
                    {!modoIconos && estaAbierta && (
                      <div className={modoPersonalizar ? undefined : 'submenu-slide'}>
                        {modoPersonalizar && !modoIconos ? (
                          <SortableContext
                            items={sec.subs.map(sub => `${sec.id}::sub::${sub.id}`)}
                            strategy={verticalListSortingStrategy}
                          >
                            {sec.subs.map(sub => (
                              <ItemArrastrable key={sub.id} id={`${sec.id}::sub::${sub.id}`}>
                                {renderSub(sec, sub)}
                              </ItemArrastrable>
                            ))}
                          </SortableContext>
                        ) : (
                          sec.subs.map(sub => (
                            <React.Fragment key={sub.id}>{renderSub(sec, sub)}</React.Fragment>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                );
                if (modoPersonalizar && !modoIconos) {
                  return <ItemArrastrable key={sec.id} id={sec.id}>{cuerpo}</ItemArrastrable>;
                }
                return <React.Fragment key={sec.id}>{cuerpo}</React.Fragment>;
              };

              if (modoPersonalizar && !modoIconos) {
                return (
                  <DndContext
                    sensors={sensoresDnD}
                    collisionDetection={closestCenter}
                    onDragEnd={(e) => {
                      if (!e.over || e.active.id === e.over.id) return;
                      const a = String(e.active.id);
                      const o = String(e.over.id);
                      if (a.includes('::sub::')) {
                        const [parent, , subA] = a.split('::');
                        const [, , subO] = o.split('::');
                        if (subA && subO) reordenarNivel2(parent, subA, subO);
                      } else {
                        reordenarNivel1(a, o);
                      }
                    }}
                  >
                    <SortableContext
                      items={seccionesVisibles.map(s => s.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      {seccionesVisibles.map((sec, idx) => renderSeccion(sec, idx))}
                    </SortableContext>
                  </DndContext>
                );
              }
              return seccionesVisibles.map((sec, idx) => renderSeccion(sec, idx));
            })()}
          </nav>

          {/* Botón personalizar — solo cuando no está en modo iconos y no personalizando */}
          {!modoIconos && !modoPersonalizar && (
            <div style={{ padding: '8px 12px 4px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
              <button
                onClick={entrarModoPersonalizar}
                style={{
                  width: '100%', padding: '8px 10px',
                  background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.85)',
                  border: 'none', borderRadius: 6,
                  fontSize: 11, fontWeight: 500, cursor: 'pointer',
                  fontFamily: "'Poppins', sans-serif",
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                }}
                title="Reordenar pestañas del menú"
              >
                ✨ Personalizar menú
              </button>
            </div>
          )}

          {/* Menú de perfil — Sidebar */}
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', position: 'relative' }}>
            <div
              onClick={() => setMenuPerfilSidebar(!menuPerfilSidebar)}
              title={modoIconos ? nombreMostrar : undefined}
              style={{
                padding: modoIconos ? '14px' : '14px 16px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                justifyContent: modoIconos ? 'center' : 'flex-start',
              }}
            >
              <div style={{ width: '34px', height: '34px', borderRadius: '50%', background: '#5c9adb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: '700', color: '#fff', flexShrink: 0 }}>
                {inicialAvatar}
              </div>
              {!modoIconos && (
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '12px', color: '#fff', fontWeight: '500', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{nombreMostrar}</div>
                  <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.7)' }}>En línea</div>
                </div>
              )}
            </div>
            {menuPerfilSidebar && !esCelular && (
              <div style={{ position: 'absolute', bottom: '100%', left: '8px', marginBottom: '8px', zIndex: 200 }}>
                <MenuPerfil
                  abierto={true}
                  onCerrar={() => setMenuPerfilSidebar(false)}
                  nombre={nombreMostrar}
                  correo={usuario}
                  rol={rolUsuario || 'Administrador'}
                  esCelular={false}
                  onMiPerfil={() => setModalPerfil(true)}
                  onCambiarPassword={() => setModalPassword(true)}
                  onCerrarSesion={() => { if (setLogueado) setLogueado(false); }}
                />
              </div>
            )}
          </div>

          {/* Popup flotante — Tablet o Desktop colapsado */}
          {modoIconos && tabletPopup && (
            <>
              <div onClick={() => setTabletPopup(null)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 149 }} />
              <div className="popup-fade" style={{
                position: 'fixed', left: (esTablet ? '68px' : '72px'),
                top: (() => {
                  const idx = seccionesVisibles.findIndex(s => s.id === tabletPopup);
                  return Math.min(idx * 48 + 70, window.innerHeight - 300) + 'px';
                })(),
                background: '#1a2836', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.1)',
                boxShadow: '0 8px 30px rgba(0,0,0,0.4)', padding: '8px 0', minWidth: '200px', zIndex: 150,
              }}>
                {(() => {
                  // PERM-FIX-TABS: seccionesVisibles asegura que el popup solo muestre subs permitidos
                  const sec = seccionesVisibles.find(s => s.id === tabletPopup);
                  if (!sec) return null;
                  return (
                    <>
                      <div style={{ padding: '8px 16px 10px', fontSize: '11px', fontWeight: '700', color: sec.color, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{sec.label}</div>
                      {sec.subs.map(sub => {
                        const ruta = sec.id + '.' + sub.id;
                        const subEsActivo = activo === ruta;
                        return (
                          <div key={sub.id} onClick={() => { setTabletPopup(null); cambiarPagina(ruta); }} style={{
                            padding: '9px 16px', cursor: 'pointer', fontSize: '12px',
                            fontWeight: subEsActivo ? '600' : '400',
                            color: subEsActivo ? sec.color : 'rgba(255,255,255,0.6)',
                            background: subEsActivo ? `${sec.color}15` : 'transparent',
                            transition: 'all 0.1s',
                          }}>
                            {sub.label}
                          </div>
                        );
                      })}
                    </>
                  );
                })()}
              </div>
            </>
          )}
        </div>
      )}

      {/* ============================================
          CONTENIDO PRINCIPAL
          ============================================ */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', paddingBottom: esCelular ? `${ALTO_BARRA_INFERIOR}px` : '0' }}>

        {/* PWA: Indicador de conexión */}
        <ConexionStatus />

        {/* TOPBAR */}
        {!modoTVActivo && <div style={{ background: '#ffffff', borderBottom: '1px solid #e0e0e0', padding: esCelular ? '12px 16px' : '12px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {esCelular && (
              <img src="/Logo Original Cuadrado.png" alt="BeepMX" style={{ width: '32px', height: '32px' }} />
            )}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: esCelular ? '14px' : '16px', fontWeight: '600', color: colores.gris }}>
                {seccionInfo && (
                  <>
                    <span style={{ color: seccionInfo.color }}>{seccionInfo.label}</span>
                    {subInfo && (
                      <>
                        <span style={{ color: colores.grisClaro, fontWeight: '400' }}>{esCelular ? ' / ' : ' › '}</span>
                        <span>{subInfo.label}</span>
                      </>
                    )}
                  </>
                )}
              </div>
              {!esCelular && (
                <div style={{ fontSize: '12px', color: colores.grisClaro }}>30 de marzo, 2026</div>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {/* Avatar en topbar */}
            <div style={{ position: 'relative' }}>
              <div onClick={() => setMenuPerfilTopbar(!menuPerfilTopbar)} style={{ width: '34px', height: '34px', borderRadius: '50%', background: colores.oscuro, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: '600', color: '#fff', cursor: 'pointer' }}>
                {inicialAvatar}
              </div>
              {menuPerfilTopbar && (
                <MenuPerfil
                  abierto={true}
                  onCerrar={() => setMenuPerfilTopbar(false)}
                  nombre={nombreMostrar}
                  correo={usuario}
                  rol={rolUsuario || 'Administrador'}
                  esCelular={esCelular}
                  onMiPerfil={() => setModalPerfil(true)}
                  onCambiarPassword={() => setModalPassword(true)}
                  onCerrarSesion={() => { if (setLogueado) setLogueado(false); }}
                />
              )}
            </div>
          </div>
        </div>}

        {/* CUERPO */}
        {!esCelular && seccionInfo && seccionInfo.subs.length > 1 && (
          <TabsSubs
            subs={seccionInfo.subs}
            subActivo={subActivo}
            onCambiarSub={(id) => setActivo(`${seccionActiva}.${id}`)}
          />
        )}
        {esCelular && seccionInfo && subInfo && (
          <div style={{
            padding: '10px 16px', background: '#fff',
            borderBottom: '1px solid #eee',
            fontSize: 14, fontWeight: 700, color: '#2f5f9e',
          }}>
            <IconoSeccion item={seccionInfo} size={14} /> {seccionInfo.label} › <IconoSeccion item={subInfo} size={14} /> {subInfo.label}
          </div>
        )}
        <div
          className={animando ? 'pagina-salida' : 'pagina-entrada'}
          // minHeight:0 -> en un flex en columna, permite que este scrollport SE ENCOJA
          // al alto disponible y haga su propio scroll vertical (en vez de crecer con el
          // contenido y que el padre overflow:hidden recorte los botones del final). Sin
          // esto, en contenido alto (cotizador en PC vertical) el pie quedaba inalcanzable.
          style={{ padding: 0, overflowY: 'auto', flex: 1, minHeight: 0 }}
        >
          {/* C2-DEFINITIVO (Opcion B): el scrollport exterior queda SIN padding para
              que cualquier modulo con position:sticky se ancle al borde real del
              viewport. El padding visual (16 movil / 24 escritorio) vive ahora en
              este wrapper interior, asi los ~40 modulos sin sticky no cambian nada. */}
          {/* El boton flotante del asistente vive fijo sobre la esquina inferior derecha. El hueco
              que ocupa se RESERVA aqui (una sola vez para las ~40 pantallas) para que ninguna fila
              quede atrapada debajo, ni siquiera con la lista al final del scroll. La medida sale de
              la geometria real del boton (AsistenteBeep.js), no de un numero copiado. */}
          <div style={{
            padding: esCelular ? 16 : 24,
            paddingBottom: (esCelular ? 16 : 24) + espacioReservadoAsistente(esCelular, ALTO_BARRA_INFERIOR),
          }}>
            {/* BANNER DE RETORNO al calendario (patron "Volver a la cotizacion"). Estatico
                arriba del contenido -> no provoca brincos de scroll. */}
            {retornoCal && (activo === 'operaciones.expedientes-todos' || activo === 'mitrabajo.mi-expediente') && (
              <button
                onClick={() => { setRetornoCal(null); setExpedienteAbrirId(null); setActivo('calendario'); }}
                style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', background: '#eaf2fb', border: '1px solid #c7daf2', color: '#185FA5', borderRadius: 10, padding: '10px 14px', marginBottom: 14, cursor: 'pointer', fontFamily: "'Poppins', sans-serif", fontSize: 14, fontWeight: 600, minHeight: 44 }}
              >
                <ArrowLeft size={16} strokeWidth={2.2} style={{ flexShrink: 0 }} /> Volver al calendario
                <span style={{ fontWeight: 400, opacity: 0.85, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>· {retornoCal.label}</span>
              </button>
            )}
            {/* PERM-3.A+B: gating del modulo activo. Las rutas legacy resuelven via ALIAS_PERMISO. */}
            {(() => {
              const claveEfectiva = ALIAS_PERMISO[activo] || activo;
              const contenido = (
                <RutaProtegida permiso={claveEfectiva}>{renderContenido()}</RutaProtegida>
              );
              if (esCelular && seccionInfo && seccionInfo.subs.length > 1) {
                return (
                  <SwipeSubs
                    subs={seccionInfo.subs}
                    subActivo={subActivo}
                    onCambiarSub={(id) => setActivo(`${seccionActiva}.${id}`)}
                  >
                    {contenido}
                  </SwipeSubs>
                );
              }
              return contenido;
            })()}
            {esCelular && seccionInfo && seccionInfo.subs.length > 1 && (
              <IndicesSubs subs={seccionInfo.subs} subActivo={subActivo} />
            )}
          </div>
        </div>
      </div>

      {/* ============================================
          NAVEGACIÓN MÓVIL COMPLETA
          ============================================ */}
      {esCelular && (
        <>
          {/* NIVEL 2 — Sheet modal de submenús con navegación interna */}
          {movilVista === 'submenus' && movilSeccion && (() => {
            // PERM-FIX-TABS: seccionesVisibles asegura que el sheet mobile solo muestre subs permitidos
            const sec = seccionesVisibles.find(s => s.id === movilSeccion);
            if (!sec) return null;
            const catIdx = seccionesVisibles.findIndex(s => s.id === movilSeccion);
            return (
              <div onClick={cerrarMenuMovil} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.55)', zIndex: 100 }}>
                <div
                  onClick={(e) => e.stopPropagation()}
                  className="sheet-up"
                  style={{
                    position: 'absolute', bottom: 0, left: 0, right: 0,
                    background: '#0f1923',
                    borderRadius: sheetExpanded ? '0' : '24px 24px 0 0',
                    padding: '0 0 env(safe-area-inset-bottom, 0px) 0',
                    maxHeight: sheetExpanded ? '100vh' : '85vh',
                    height: sheetExpanded ? '100vh' : 'auto',
                    display: 'flex', flexDirection: 'column',
                    transform: sheetY !== 0 ? `translateY(${sheetY}px)` : undefined,
                    transition: sheetDragging ? 'none' : 'all 0.3s cubic-bezier(0.32,0.72,0,1)',
                  }}
                >
                  {/* Handle de arrastre — más visible */}
                  <div
                    onTouchStart={onSheetTouchStart}
                    onTouchMove={onSheetTouchMove}
                    onTouchEnd={onSheetTouchEnd}
                    style={{ display: 'flex', justifyContent: 'center', padding: '14px 0 6px', flexShrink: 0, cursor: 'grab', touchAction: 'none' }}
                  >
                    <div style={{ width: '40px', height: '4px', borderRadius: '2px', background: '#ccc' }} />
                  </div>

                  {/* Header */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 16px 12px', flexShrink: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <IconoSeccion item={sec} size={24} color={sec.color} />
                      <span style={{ fontSize: '18px', fontWeight: '700', color: sec.color, fontFamily: PP }}>{sec.label}</span>
                    </div>
                    <div onClick={cerrarMenuMovil} style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '14px', color: 'rgba(255,255,255,0.5)' }}>
                      ✕
                    </div>
                  </div>

                  {/* Puntos indicadores de categoría */}
                  <div style={{ display: 'flex', justifyContent: 'center', gap: '6px', padding: '0 0 12px', flexShrink: 0 }}>
                    {seccionesVisibles.map((s, i) => (
                      <div
                        key={s.id}
                        onClick={() => { setMovilCatSlideDir(i > catIdx ? 'left' : 'right'); setMovilSeccion(s.id); }}
                        style={{
                          width: s.id === movilSeccion ? '18px' : '6px',
                          height: '6px',
                          borderRadius: '3px',
                          background: s.id === movilSeccion ? sec.color : 'rgba(255,255,255,0.2)',
                          transition: 'all 0.2s',
                          cursor: 'pointer',
                        }}
                      />
                    ))}
                  </div>

                  {/* Grid de submenús */}
                  <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', overscrollBehavior: 'contain' }}>
                    <div
                      key={'cat-' + movilSeccion}
                      className={movilCatSlideDir === 'left' ? 'slide-in-right' : movilCatSlideDir === 'right' ? 'slide-in-left' : ''}
                      onTouchStart={onCatSwipeStart}
                      onTouchEnd={onCatSwipeEnd}
                      style={{ padding: '0 16px 24px', display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}
                    >
                      {sec.subs.map((sub, idx) => {
                        const ruta = sec.id + '.' + sub.id;
                        const esActv = activo === ruta;
                        const tieneModulo = rutasConModulo[ruta];
                        return (
                          <div
                            key={sub.id}
                            className="card-pop"
                            onClick={() => entrarSubMenu(sec.id, idx)}
                            style={{
                              background: esActv ? `${sec.color}20` : 'rgba(255,255,255,0.05)',
                              borderRadius: '16px',
                              padding: '20px 16px',
                              cursor: 'pointer',
                              border: esActv ? `2px solid ${sec.color}` : '2px solid rgba(255,255,255,0.06)',
                              textAlign: 'center',
                              opacity: tieneModulo ? 1 : 0.55,
                              animationDelay: (idx * 0.05) + 's',
                              position: 'relative',
                              transition: 'background 0.15s, border 0.15s',
                            }}
                          >
                            <div style={{ marginBottom: '10px' }}><IconoSeccion item={sub} size={40} color={esActv ? sec.color : 'rgba(255,255,255,0.8)'} /></div>
                            <div style={{ fontSize: '13px', fontWeight: esActv ? '700' : '500', color: esActv ? sec.color : 'rgba(255,255,255,0.8)', fontFamily: PP }}>{sub.label}</div>
                            {!tieneModulo && (
                              <div style={{ position: 'absolute', top: '8px', right: '8px', fontSize: '8px', padding: '2px 8px', borderRadius: '8px', background: `${sec.color}25`, color: sec.color, fontWeight: '700', fontFamily: PP }}>Pronto</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* PANEL "MÁS" — Sheet modal con todas las secciones */}
          {movilVista === 'secciones' && (
            <div onClick={cerrarMenuMovil} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.55)', zIndex: 100 }}>
              <div
                onClick={(e) => e.stopPropagation()}
                className="sheet-up"
                style={{
                  position: 'absolute', bottom: 0, left: 0, right: 0,
                  background: '#0f1923',
                  borderRadius: sheetExpanded ? '0' : '24px 24px 0 0',
                  padding: '0 0 env(safe-area-inset-bottom, 0px) 0',
                  maxHeight: sheetExpanded ? '100vh' : 'auto',
                  height: sheetExpanded ? '100vh' : 'auto',
                  display: 'flex', flexDirection: 'column',
                  transform: sheetY !== 0 ? `translateY(${sheetY}px)` : undefined,
                  transition: sheetDragging ? 'none' : 'all 0.3s cubic-bezier(0.32,0.72,0,1)',
                }}
              >
                {/* Handle */}
                <div
                  onTouchStart={onSheetTouchStart}
                  onTouchMove={onSheetTouchMove}
                  onTouchEnd={onSheetTouchEnd}
                  style={{ display: 'flex', justifyContent: 'center', padding: '14px 0 6px', cursor: 'grab', touchAction: 'none' }}
                >
                  <div style={{ width: '40px', height: '4px', borderRadius: '2px', background: '#ccc' }} />
                </div>

                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 20px 16px' }}>
                  <span style={{ fontSize: '18px', fontWeight: '700', color: '#fff', fontFamily: PP }}>Menú completo</span>
                  <div onClick={cerrarMenuMovil} style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '14px', color: 'rgba(255,255,255,0.5)' }}>
                    ✕
                  </div>
                </div>

                {/* Grid 3x3 */}
                <div style={{ padding: '0 16px 28px', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', flex: 1, overflowY: 'auto' }}>
                  {seccionesVisibles.map((sec, idx) => {
                    const esActiva = seccionActiva === sec.id;
                    return (
                      <div
                        key={sec.id}
                        className="card-pop"
                        onClick={() => abrirSubMenus(sec.id)}
                        style={{
                          background: esActiva ? `${sec.color}18` : 'rgba(255,255,255,0.05)',
                          borderRadius: '16px',
                          padding: '18px 8px',
                          cursor: 'pointer',
                          border: esActiva ? `2px solid ${sec.color}40` : '2px solid transparent',
                          textAlign: 'center',
                          animationDelay: (idx * 0.04) + 's',
                          transition: 'background 0.15s',
                        }}
                      >
                        <div style={{ marginBottom: '8px' }}><IconoSeccion item={sec} size={28} color={esActiva ? sec.color : 'rgba(255,255,255,0.75)'} /></div>
                        <div style={{ fontSize: '11px', fontWeight: esActiva ? '700' : '500', color: esActiva ? sec.color : 'rgba(255,255,255,0.75)', fontFamily: PP }}>{sec.label}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* DIÁLOGO "¿Salir de Beep OS?" */}
          {dialogoSalir && (
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
              <div style={{ background: '#1a2836', borderRadius: '20px', padding: '28px 24px', maxWidth: '320px', width: '100%', textAlign: 'center', boxShadow: '0 12px 40px rgba(0,0,0,0.5)' }}>
                <div style={{ fontSize: '36px', marginBottom: '12px' }}>👋</div>
                <div style={{ fontSize: '18px', fontWeight: '700', color: '#fff', fontFamily: PP, marginBottom: '8px' }}>¿Salir de Beep OS?</div>
                <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.5)', fontFamily: PP, marginBottom: '24px' }}>¿Estás seguro que deseas salir de la aplicación?</div>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button onClick={() => { setDialogoSalir(false); window.history.pushState({ beepMenu: true }, ''); }} style={{ flex: 1, padding: '12px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.15)', background: 'transparent', color: '#fff', fontSize: '14px', fontWeight: '600', cursor: 'pointer', fontFamily: PP }}>
                    Cancelar
                  </button>
                  <button onClick={() => { setDialogoSalir(false); window.history.back(); }} style={{ flex: 1, padding: '12px', borderRadius: '12px', border: 'none', background: colores.rojo, color: '#fff', fontSize: '14px', fontWeight: '600', cursor: 'pointer', fontFamily: PP }}>
                    Salir
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* BARRA INFERIOR FIJA — basada en favoritos del usuario (máx 4 + botón Más) */}
          {/* Orden: respeta el orden del menú lateral (drag personal o default), no el orden de marcado */}
          {!overlayActivo && (() => {
            const favoritosSet = new Set(menuFavoritos);
            const favsVisibles = seccionesVisibles
              .filter(s => favoritosSet.has(s.id))
              .slice(0, 4);
            const sinFavoritos = favsVisibles.length === 0;
            return (
              <div style={{
                position: 'fixed', bottom: 0, left: 0, right: 0,
                background: '#4e5357',
                display: 'flex',
                borderTop: '1px solid rgba(255,255,255,0.12)',
                zIndex: 99,
                paddingBottom: 'env(safe-area-inset-bottom, 0px)',
                height: `${ALTO_BARRA_INFERIOR}px`,
                alignItems: 'center',
              }}>
                {sinFavoritos ? (
                  <div
                    onClick={() => {
                      if (movilVista === 'secciones') cerrarMenuMovil();
                      else { setMovilVista('secciones'); setMenuAbierto(true); }
                    }}
                    style={{
                      flex: 1, display: 'flex', alignItems: 'center',
                      justifyContent: 'center', gap: 12, cursor: 'pointer', height: '100%',
                      padding: '0 16px',
                    }}
                  >
                    <div style={{
                      width: 38, height: 38, borderRadius: 14,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 22, color: '#fff',
                    }}>
                      {movilVista === 'secciones' ? '✕' : '⊞'}
                    </div>
                    <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.85)', fontFamily: PP, lineHeight: 1.3 }}>
                      Marca tus favoritos desde PC para accesos rápidos
                    </span>
                  </div>
                ) : (
                  <>
                    {favsVisibles.map(sec => {
                      const esActiva = seccionActiva === sec.id && !movilVista;
                      return (
                        <div key={sec.id} onClick={() => abrirSubMenus(sec.id)} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', height: '100%' }}>
                          <div style={{
                            width: esActiva ? '42px' : '38px', height: esActiva ? '42px' : '38px',
                            borderRadius: '14px',
                            background: esActiva ? sec.color : 'transparent',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: esActiva ? '20px' : '22px',
                            transition: 'all 0.25s cubic-bezier(0.32,0.72,0,1)',
                            boxShadow: esActiva ? `0 4px 14px ${sec.color}50` : 'none',
                          }}>
                            <IconoSeccion item={sec} size={esActiva ? 20 : 22} color={esActiva ? '#fff' : 'rgba(255,255,255,0.7)'} />
                          </div>
                          {esActiva && (
                            <span style={{ fontSize: '9px', color: sec.color, fontWeight: '700', marginTop: '2px', fontFamily: PP }}>{sec.label}</span>
                          )}
                        </div>
                      );
                    })}
                    {/* Botón Más */}
                    <div onClick={() => {
                      if (movilVista === 'secciones') {
                        cerrarMenuMovil();
                      } else {
                        setMovilVista('secciones');
                        setMenuAbierto(true);
                      }
                    }} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', height: '100%' }}>
                      <div style={{
                        width: '38px', height: '38px', borderRadius: '14px',
                        background: movilVista === 'secciones' ? 'rgba(255,255,255,0.2)' : 'transparent',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '22px',
                        transition: 'all 0.25s',
                      }}>
                        {movilVista === 'secciones' ? '✕' : '⊞'}
                      </div>
                      {movilVista === 'secciones' && (
                        <span style={{ fontSize: '9px', color: '#fff', fontWeight: '600', marginTop: '2px', fontFamily: PP }}>Cerrar</span>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })()}
        </>
      )}

      {/* Modales de perfil */}
      <ModalPerfil
        abierto={modalPerfil}
        onCerrar={() => setModalPerfil(false)}
        nombre={nombreMostrar}
        correo={usuario}
        telefono=""
        area={usuarioObj?.area || ''}
        rol={rolUsuario || 'Administrador'}
      />
      <ModalPassword
        abierto={modalPassword}
        onCerrar={() => setModalPassword(false)}
      />

      {/* Asistente flotante Beep */}
      <AsistenteBeep
        onNavegar={(ruta, tab) => navegarConTab(ruta, tab)}
        usuario={usuario}
        rol={rolUsuario}
        nombre={nombreMostrar}
        /* MOVIL-GUARDAR: con una ventana modal abierta el boton flotante tapaba sus controles.
           `overlayActivo` ya detectaba ese caso (<html> con overflow:hidden) para esconder la
           barra inferior; se reusa la MISMA senal en vez de inventar otra. */
        oculto={overlayActivo}
      />

      {/* INSTALAR-APP: el aviso de "instala la app", solo la primera vez y solo en movil. El propio
          componente decide si toca enseñarlo; aqui solo se le dice quien es y cuanto mide la barra
          inferior, para que no se monte encima. Con una ventana modal abierta se esconde, igual
          que el boton flotante. */}
      {!overlayActivo && esCelular && (
        <BarraInstalar usuarioId={usuarioObj?.id} altoBarraInferior={ALTO_BARRA_INFERIOR} />
      )}
    </div>
    </div>
  );
}

export default Dashboard;
